import dayjs from '../dayjs';
import {find, keyBy} from 'lodash/collection';
import {cloneDeep, isEqual} from 'lodash/lang';
import {getDepartureTimeAsSecondsSinceMidnight, getDistanceInMetres, normaliseDate} from '../libs/routes-lib';
import {ulid} from 'ulid';
import {findIndex, last} from 'lodash/array';
import {getCachedDirections, getCachedPath, getDirections} from '../libs/pathLib';
import {BusRoute, Trip} from './busRoute';
import {getSecondsSinceMidnightAsDayjs, toHrsMinsSecs, toTime} from '../libs/formatLib';
import {pickBy, values} from 'lodash/object';
import {chunk, clone, concat, every, intersectionWith, keys, merge, pick, uniqWith} from 'lodash';
import {eachSeries} from 'async';
import config from '../config';
import {CharterRouteRun, Deadrun} from './deadrun';
import {mapSeries} from 'async/index';
import {secsSinceMidnightToDayjs} from './timeFilter';
import {DATE_STRING, OperatingDays} from './schedule';
import {DEFAULT_DRIVER_RATE} from './hrm/employee';
import { getRowValidation } from '../utils/shiftBatValidation';
import { toLineString } from '../libs/mapLib';

const toNumber = str => str.replace(/\D/g, '');


export class TimeBlock {
    constructor({id, type, startTime, endTime}) {
        this.id = id;
        this.startTime = startTime;
        this.endTime = endTime;
        this.type = type; // work or break
    }

    getDuration() {
        return this.endTime.diff(this.startTime, 'minute');
    }

    getStartTime() {
        return this.startTime;
    }

    getEndTime() {
        return this.endTime;
    }
}

/**
 * Converts the workItems to timeblocks, and the time between workItems to break timeBlocks
 * @param workItems
 */
export const convertWorkItemsToTimeBlocks = (workItems) => {
    let timeBlocks = [];
    // let lastTime = null;
    workItems.forEach((workItem, i) => {
        // const startTime = workItem.getStartTime();
        // const endTime = workItem.getEndTime();
        // if (lastTime) {
        //     const breakBlock = new TimeBlock({type: 'break', startTime: lastTime, endTime: startTime});
        //     timeBlocks.push(breakBlock);
        // }
        timeBlocks = timeBlocks.concat(workItem.toTimeBlocks());
        // lastTime = endTime;
    });
    // remove any first timeblocks that are of type break
    while (timeBlocks[0]?.type === 'break') {
        timeBlocks.shift();
    }
    timeBlocks = timeBlocks.filter(tb => tb.startTime && tb.endTime);
    return timeBlocks;
};

export class Service {
    static build({routeId, tripId, route, allStops}) {
        if (!route) {
            throw new Error('No route %s with id ', route.routeNumber, routeId);
        }
        const service = clone(route);
        const trip = find(route.services, trip => trip.tripId === tripId);
        if (!trip) {
            throw new Error('No trip on route %s with id ', route.routeNumber, tripId);
        }
        service.setBaseStops(allStops, trip.stopTimes);
        return service;
    }

}

export const combineDeadruns = deadruns => {
    return deadruns.reduce((deadrun, next) => {
        if (!next?.waypoints) {
            return deadrun;
        }
        if (!deadrun?.waypoints) {
            return next;
        }
        deadrun.waypoints = deadrun.waypoints.concat(next.waypoints);
        deadrun.duration += next.duration;
        deadrun.totalDuration += next.totalDuration;
        deadrun.idleTime += next.idleTime;
        deadrun.distance += next.distance;
        deadrun.endLocation = next.endLocation;
        return deadrun;
    }, null);
};

/**
 *
 * @param deadruns
 * @param sortFn to sort the deadruns. If not provided, the deadrun with the shortest distance is returned
 * @returns deadrun based on the sortFn or distance
 */
const calculateBestDeadrun = (deadruns, sortFn) => deadruns.reduce((best, deadrun) => {
    if (!best || (sortFn && sortFn(deadrun, best)) || deadrun?.distance < best.distance) {
        return deadrun;
    }
    return best;
}, null);

/**
 * Returns a deadrun if it is possible to travel from startLocation to endLocation in the allowed time frame
 * @param startLocation
 * @param endLocation
 * @param totalDuration totalDuration duration to perform this deadrun (including idle time)
 * @returns {Promise<{totalDuration: number, duration: number, distance: number, startLocation, idleTime: number, waypoints, endLocation}>}
 */
const getDeadrun = async (startLocation, endLocation, totalDuration = 0) => {
    if (!startLocation || !endLocation) {
        return {
            totalDuration, // Total time in secs between actual end of actual start of next job
            idleTime: 0, // Total time the driver/vehicle is sitting idle
            duration: 0, // The duration of the deadrun in seconds (when vehicle is moving)
            distance: 0, // The distance of the deadrun
            waypoints: [], startLocation, endLocation,
        };
    }
    const {waypoints, duration, distance} = await getCachedPath('bus', startLocation, endLocation);
    totalDuration = totalDuration > duration ? totalDuration : duration;
    const idleTime = totalDuration - duration;
    if (idleTime > 0) {
        return {
            totalDuration, // Total time in secs between actual end of actual start of next job
            idleTime, // Total time the driver/vehicle is sitting idle
            duration, // The duration of the deadrun in seconds (when vehicle is moving)
            distance, // The distance of the deadrun
            waypoints, startLocation, endLocation,
        };
    }
};

export const createDeadRun = async (fromStop, toStop, allStops, allDeadruns) => {
    const routeId = `${fromStop.stopId}#${toStop.stopId}#Special - ${fromStop.stopName} to ${toStop.stopName}`;
    const existing = allDeadruns[routeId];
    if (existing) {
        return {
            route: existing,
            duration: existing.duration,
            distance: existing.distance
        };
    }
    let dead = new BusRoute({
        routeId,
        routeName: `Special - ${fromStop.stopName} to ${toStop.stopName}`
    });
    await dead.addStop(fromStop, 'append');
    await dead.addStop(toStop, 'append');
    dead.setBaseStops(allStops);

    // dead = await deadrunModelData.save(dead);
    const result = {
        route: dead,
        duration: dead.duration,
        distance: dead.distance
    };
    return result;
};

export const createDeadrunRow = async (start, end, allStops, allDeadruns) => {
    const {route: dead, duration, distance} = await createDeadRun(start, end, allStops, allDeadruns);
    const deadRow = new ShiftBatDeadRow({
        type: ShiftBatRowType.dead,
        route: dead,
        routeId: dead.routeId,
        start,
        end,
        distance,
        duration,
    });
    return deadRow;
}

export class ShiftBat {

    static fromBlock = ({block, allStops, allRoutes, operator}) => {
        const routesByBlockTripId = {};
        const tripsByBlockTripId = {};
        block.trips.forEach(trip => {
            const route = allRoutes[trip.routeId];
            routesByBlockTripId[trip.tripId] = route;
            tripsByBlockTripId[trip.tripId] = route.services.find(service => service.tripId.split('|').some(tripId => trip.tripId === tripId));
        });
        const shiftBat = new ShiftBat({
            shiftBatId: block.blockId,
            shiftBatName: block.blockName,
            rows: block.trips.map(trip => {
                const busableRoute = routesByBlockTripId[trip.tripId];
                const busableTrip = tripsByBlockTripId[trip.tripId];
                const stopRows =busableTrip.stopTimes
                        .filter((st) => operator?.opts?.shiftbat?.turnsOnAllStops || st.timingPoint)
                        .map((st) => {
                            return {
                                stopTimeId: st.stopTimeId,
                                stopId: st.stopId,
                                tripId: busableTrip.tripId,
                                routeId: busableRoute.routeId,
                                time: st.departSecs,
                                title: allStops[st.stopId]?.stopName || '',
                                sequence: busableTrip.getStopSequence(st, allStops)
                            };
                        });
                return new ShiftBatServiceRow({
                    type: ShiftBatRowType.service,
                    routeId: busableRoute.routeId,
                    route: busableRoute,
                    tripId: busableTrip.tripId,
                    trip: busableTrip,
                    stopRows
                });
            })
        });
        return shiftBat;
    }

    static fromCharterItinerary = async ({charter, depot, endDepot, allStops, allTransfers, allRoutes, allDeadruns, deadrunModelData, preDepartureCheck = 600, postDepartureCheck = 600, routes = [], forceDeadrunToFit = -1}) => {
            // filter out itinerary with no stops (empty legs)
            const itinerary = routes.length ? routes : charter.itinerary.filter(route => route.stops.length > 0);

            let rows = [];
            let startDepotNoteRow, depotStopRow, startDeadRow;
            // Add initial depot if exists
            if (depot) {
                startDepotNoteRow = {type: ShiftBatRowType.noteTimed, title: 'Start Depot', time: -1}
                rows.push(startDepotNoteRow);
                depotStopRow = {
                    type: ShiftBatRowType.stop,
                    description: 'Depart Depot',
                    stopId: depot?.stopId,
                    stopTimeId: ulid(),
                    stop: depot,
                    time: -1
                };
                rows.push(depotStopRow);
            }

            // Process itinerary if it exists and has valid structure
            if (itinerary?.[0]?.stops?.length && itinerary?.[0]?.services?.[0]?.stopTimes?.length) {

                // Add initial dead run from depot to first stop if needed
                if (depot) {
                    startDeadRow = await createDeadrunRow(depot, itinerary[0].stops[0], allStops, allDeadruns);
                    await deadrunModelData.save(startDeadRow.route);
                    rows.push(startDeadRow);
                }

                // Process each route in the itinerary
                await eachSeries(itinerary, async (route) => {
                    const i = itinerary.indexOf(route);
                    const serviceRow = new ShiftBatServiceRow({
                        type: ShiftBatRowType.service,
                        route,
                        trip: route.services[0],
                        title: route.routeName,
                    });
                    await serviceRow.updateRow({allStops});
                    rows.push(serviceRow);

                    const startTime = serviceRow?.trip?.stopTimes?.[0]?.arriveSecs || -1;
                    if(startTime > -1 && depotStopRow && depotStopRow.time < 0 && Number.isFinite(startDeadRow?.duration)) {
                        depotStopRow.time = startTime - startDeadRow.duration;
                    }
                    if(startDepotNoteRow && startDepotNoteRow.time < 0) {
                        startDepotNoteRow.time = depotStopRow.time - preDepartureCheck;
                    }

                    if (i < itinerary.length - 1) {
                        const nextRoute = itinerary[i + 1];
                        // Add dead run between routes if needed
                        if (last(route.stops)?.stopId !== nextRoute.stops[0]?.stopId) {
                            const {route: dead, duration, distance} = await createDeadRun(
                                last(route.stops),
                                nextRoute.stops[0], 
                                allStops,
                                allDeadruns
                            );

                            const deadStartTime = last(route.services[0].stopTimes).departSecs;
                            const nextRouteStartTime = nextRoute.services[0].stopTimes[0].arriveSecs;
                            const arriveTimeAfterDeadrun = deadStartTime + duration;
                            let breakTime = 0;
                            if (arriveTimeAfterDeadrun > nextRouteStartTime) {
                                const durationAdjustment = arriveTimeAfterDeadrun - nextRouteStartTime;
                                dead.duration -= durationAdjustment;
                            } else {
                                breakTime = nextRouteStartTime - arriveTimeAfterDeadrun - 60;
                            }

                            const deadRow = new ShiftBatDeadRow({
                                type: ShiftBatRowType.dead,
                                time: deadStartTime,
                                route: dead,
                                start: last(route.stops),
                                end: nextRoute.stops[0],
                                distance,
                                duration: dead.duration
                            });
                            rows.push(deadRow);

                            if (breakTime > 15 * 60) {
                                const breakRow = new ShiftBatBreakRow({
                                    type: ShiftBatRowType.break,
                                    time: deadStartTime + deadRow.duration + 60,
                                    title: `Break for ${toHrsMinsSecs(breakTime, false, true)}`,
                                    duration: Math.round(breakTime)
                                });
                                rows.push(breakRow);
                            }
                        }
                    }
                });

                // Add final dead run to depot if needed
                if (endDepot) {
                    const lastRow = last(rows);
                    const lastStop = last(last(itinerary).stops);
                    const deadRow = await createDeadrunRow(lastStop, endDepot, allStops, allDeadruns);
                    await deadrunModelData.save(deadRow.route);
                    rows.push(deadRow);

                    // 1 min buffer
                    const buffer = 60;

                    // calculate stop time by adding the duration of the last row to the time of the last row
                    const stopTime = (lastRow?.getEndTime() || 0) + (deadRow.duration || 0) + buffer;

                    // Add final depot stop
                    rows.push({
                        type: ShiftBatRowType.stop,
                        description: 'Arrive Depot',
                        time: stopTime,
                        stopId: endDepot.stopId,
                        stop: endDepot
                    });
                }
            }

            // Add end depot note
            if (endDepot) {
                rows.push({type: ShiftBatRowType.noteTimed, time: (last(rows)?.time || 0) + (last(rows).duration || 0) + postDepartureCheck, title: 'End Depot'});
            }


            // Create and initialize ShiftBat

            // Handle unique shiftBatNumber for the charter
            const generateUniqueShiftBatNumber = (baseNumber, duties, counter = 1) => {
                const cleanBaseNumber = baseNumber.split('-')[0];
                const candidateNumber = `${cleanBaseNumber}-${counter}`;
                const existingDuty = duties.find(duty => duty.shiftBatNumber === candidateNumber);
                
                if (!existingDuty) {
                    return { number: candidateNumber, counter };
                }
                
                return generateUniqueShiftBatNumber(baseNumber, duties, counter + 1);
            };
            
            const baseShiftBatNumber = charter.itinerary?.[0]?.routeNumber || 'CHA';
            const { number: shiftBatNumber, counter } = generateUniqueShiftBatNumber(baseShiftBatNumber, charter.duties);
            const shiftBatName = counter === 1 ? charter.name : `${charter.name} - ${counter}`;


            const sb = new ShiftBat({
                shiftBatNumber: shiftBatNumber,
                shiftBatName: shiftBatName,
                rows,
                charter: true,
                effectiveDate: charter.startDate,
                dates: charter.itinerary[0]?.services[0]?.dates,
                scheduleIds: charter.itinerary[0]?.services[0]?.scheduleIds
            });

            sb.updateRows({allDeadruns, allStops, allTransfers, allRoutes});

            return sb;
    };

    static fromCharterDetails = async ({charter, depot, allDeadruns, roundtrip}) => {
        let rows = [{type: ShiftBatRowType.noteTimed, title: 'Start Depot'}];
        if (depot) {
            rows.push({
                type: ShiftBatRowType.stop,
                time: charter.startTime - 3 * 60,
                description: 'Depart Depot',
                stopId: depot.stopId,
                stop: depot
            });
        }
        if (charter.destinations?.length) {

            if (depot) {
                // let distance = getDistanceInMetres(depot, charter.destinations[0]);
                // let duration = distance / 14;
                const dead = new BusRoute({
                    routeId: ulid(), routeName: `Special - ${depot.stopName} to ${charter.destinations[0].stopName}`
                });
                await dead.addStop(depot, 'append');
                await dead.addStop(charter.destinations[0], 'append');
                const duration = dead.duration;
                const distance = dead.distance;

                rows.push(new ShiftBatDeadRow({
                    type: ShiftBatRowType.dead,
                    time: charter.destinations[0].time - duration,
                    route: dead,
                    start: depot,
                    end: charter.destinations[0],
                    distance,
                    duration,
                }));
            }
            const routeId = ulid();
            const route = new BusRoute({
                routeId,
                routeNumber: 'CHA',
                routeName: charter.name,
                services: [new Trip({routeId, stopTimes: [], stops: []})]
            });

            await Promise.all(charter.destinations.map(async (destination, i) => {
                await route.addStop(destination, 'append');
                route.stopTimes[i].arriveSecs = destination.time;
                route.stopTimes[i].departSecs = destination.time;
                route.stops[i].arriveSecs = destination.time;
                route.stops[i].departSecs = destination.time;
                route.services[0].stopTimes[i].arriveSecs = destination.time;
                route.services[0].stopTimes[i].departSecs = destination.time;
                route.services[0].stops[i].arriveSecs = destination.time;
                route.services[0].stops[i].departSecs = destination.time;
                // if (i > 0) {
                //     const distance = getDistanceInMetres(charter.destinations[i - 1], destination);
                //     const duration = distance / 14;
                //
                //     rows.push({
                //         type: ShiftBatRowType.dead,
                //         start: charter.destinations[i - 1],
                //         end: destination,
                //         distance,
                //         duration,
                //     });
                // }
                // rows.push({
                //     type: ShiftBatRowType.stop,
                //     stop: {...destination, stopName: destination.name}
                // });

            }));

            // if (roundtrip) {
            //
            //     await Promise.all(charter.destinations.reverse().map(async (destination, i) => {
            //         await route.addStop(destination, 'append');
            //         route.stopTimes[i].arriveSecs = destination.time + 3600;
            //         route.stopTimes[i].departSecs = destination.time + 3600;
            //         route.stops[i].arriveSecs = destination.time + 3600;
            //         route.stops[i].departSecs = destination.time;
            //         route.services[0].stopTimes[i].arriveSecs = destination.time;
            //         route.services[0].stopTimes[i].departSecs = destination.time;
            //         route.services[0].stops[i].arriveSecs = destination.time;
            //         route.services[0].stops[i].departSecs = destination.time;
            //         // if (i > 0) {
            //         //     const distance = getDistanceInMetres(charter.destinations[i - 1], destination);
            //         //     const duration = distance / 14;
            //         //
            //         //     rows.push({
            //         //         type: ShiftBatRowType.dead,
            //         //         start: charter.destinations[i - 1],
            //         //         end: destination,
            //         //         distance,
            //         //         duration,
            //         //     });
            //         // }
            //         // rows.push({
            //         //     type: ShiftBatRowType.stop,
            //         //     stop: {...destination, stopName: destination.name}
            //         // });
            //
            //     }));
            //
            // }

            rows.push({
                type: ShiftBatRowType.charter,
                route,
                trip: route.services[0],
                title: charter.name,
                description: charter.description,
            });

            if (depot) {
                // let distance = getDistanceInMetres(last(charter.destinations), depot);
                // let duration = distance / 14;

                const dead = new BusRoute({
                    routeId: ulid(), routeName: `Special - ${charter.destinations[0].stopName} to ${depot.stopName}`
                });
                await dead.addStop(last(charter.destinations), 'append');
                await dead.addStop(depot, 'append');
                const duration = dead.duration;
                const distance = dead.distance;
                rows.push({
                    type: ShiftBatRowType.dead,
                    time: last(charter.destinations).time + duration,
                    end: depot,
                    start: last(charter.destinations),
                    distance,
                    duration,
                });
            }
        }
        if (depot) {
            rows.push({
                type: ShiftBatRowType.stop, description: 'Arrive Depot', stopId: depot.stopId, stop: depot
            });
        }
        // {type: ShiftBatRowType.stop, time: charter.startTime - 3 * 60, description: 'Depart Depot', stopId: depotId},
        // {type: ShiftBatRowType.stop, time: charter.endTime + 3 * 60, description: 'Arrive Depot', stopId: depotId},
        rows.push({type: ShiftBatRowType.noteTimed, time: last(rows).time + 600, title: 'End Depot'});
        // if (!depotId) {
        //     rows.splice(1, 2);
        // }
        const sb = new ShiftBat({
            shiftBatNumber: 'CHA', shiftBatName: charter.name, rows, charter: true, effectiveDate: charter.startDate
        });
        await sb.updateRows({allDeadruns});
        return sb;
    };

    static from(data) {
        return new ShiftBat({...data});
    }

    constructor(props) {
        this.shiftBatId = ulid();
        this.shiftBatNumber = 'NEW';
        this.shiftBatName = '';
        this.shiftBatDetails = '';
        this.shiftBatColour = '#007bff';
        this.shiftBatLogo = null;
        this.createdAt = dayjs();
        this.effectiveDate = dayjs();
        this.scheduleIds = []; //Schedules
        this.dates = []; // Dates for shift
        this.rows = []; // [ShiftBatRow]
        this.forceUpdate = true;
        this.charter = false;
        Object.assign(this, props);
        // if (!this.rows.length) {
        //     this.rows = [new ShiftBatRow({title: 'First thing', description: 'Your description...', time: 6 * 60 * 60})]
        // }

        if (this.rows.length) {
            this.rows = this.rows.map(row => this.createRow(row));
        }
        if (typeof this.createdAt === 'string') {
            this.createdAt = dayjs(this.createdAt, DATE_STRING);
        }
        if (typeof this.updatedAt === 'string') {
            this.updatedAt = dayjs(this.updatedAt, DATE_STRING);
        }
        if (typeof this.effectiveDate === 'string') {
            this.effectiveDate = dayjs(this.effectiveDate, DATE_STRING);
        }
        if (this.dates) {
            this.dates = this.dates.map(date => normaliseDate(date));
        }
    }

    getName() {
        return this.shiftBatNumber;
    }

    clone(field, value) {
        const newShiftBat = ShiftBat.from({
            ...this, rows: this.rows.map(r => r.clone()),
        });
        if (field?.length) {
            newShiftBat[field] = value;
        }
        return newShiftBat;
    }

    cloneDeep(field, value) {
        const newShiftBat = ShiftBat.from({
            ...this, rows: this.rows.map(r => r.cloneDeep()),
        });
        if (field?.length) {
            newShiftBat[field] = value;
        }
        return newShiftBat;
    }

    // Validation helpers
    hasAtLeastOneServiceRow() {
        return !!find(this.rows, {type: ShiftBatRowType.service});
    }

    hasPositiveShiftTime() {
        return this.getShiftTime() > 0;
    }

    areAllRowsValid(args) {
        return this.rows?.every((r, i) => r.isValid(args, i));
    }

    isNonDecreasingTimes() {
        if (!this.rows?.length) return false;
        return every(this.rows.slice(1), (row, i) => (row.getTime() && this.rows[i].getEndTime() ? row.getTime() >= this.rows[i].getEndTime() : true));
    }

    isValid(args) {
        // valid if there is at least one service row, and all rows are valid
        return this.rows?.length && this.hasPositiveShiftTime() && this.hasAtLeastOneServiceRow() && this.areAllRowsValid(args) && this.isNonDecreasingTimes();
    }

    getStartLocation() {
        return this.rows.filter(row => row.getStartLocation)?.[0]?.getStartLocation();
    }

    getEndLocation() {
        return last(this.rows.filter(row => row.getEndLocation))?.getEndLocation();
    }

    getTrailingDeadrun() {
        for (let i = this.rows.length - 1; i >= 0; i--) {
            const row = this.rows[i];
            if (row.type === ShiftBatRowType.service) {
                return;
            } else if (row.type === ShiftBatRowType.dead) {
                return row.route;
            }
        }
    }

    getLeadingDeadrun() {
        for (let i = 0; i < this.rows.length; i++) {
            const row = this.rows[i];
            if (row.type === ShiftBatRowType.service) {
                return;
            } else if (row.type === ShiftBatRowType.dead) {
                return row.route;
            }
        }
    }

    getPrevTimedRow(idx) {
        if (idx === 0) {
            return;
        }
        for (let i = idx - 1; i >= 0; i--) {
            const row = this.rows[i];
            if (Number.isFinite(row.time) && row.time > -1 && [ShiftBatRowType.service, ShiftBatRowType.stop, ShiftBatRowType.noteTimed, ShiftBatRowType.dead, ShiftBatRowType.break, ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken].includes(row.type)) {
                return row;
            }
        }
    }

    getFirstStopRow() {
        for (let i = 0; i < this.rows.length; i++) {
            const row = this.rows[i];
            if (row.type === ShiftBatRowType.service) {
                return;
            } else if (row.type === ShiftBatRowType.stop) {
                return row;
            }
        }
    }

    getLastStopRow() {
        for (let i = this.rows.length - 1; i >= 0; i--) {
            const row = this.rows[i];
            if (row.type === ShiftBatRowType.service) {
                return;
            } else if (row.type === ShiftBatRowType.stop) {
                return row;
            }
        }
    }

    getStartTime(opts = {date: dayjs(), asDayJs: false}) {
        const {date, asDayJs} = opts;
        const firstTimedRow = this.rows.filter(r => r.getTime() >= 0)[0];
        const time = firstTimedRow?.getTime() || 0;
        if (asDayJs) {
            return secsSinceMidnightToDayjs(time, date);
        }
        return time;
    }

    getEndTime(opts = {date: dayjs(), asDayJs: false}) {
        const {date, asDayJs} = opts;
        const lastTimedRow = last(this.rows.filter(r => r.getEndTime() >= 0));
        const time = lastTimedRow?.getEndTime() || 0 + lastTimedRow?.duration || 0;
        if (asDayJs) {
            return secsSinceMidnightToDayjs(time, date);
        }
        return time;
    }

    getActualStartTime(opts = {date: dayjs(), asDayJs: false}) {
        const {date, asDayJs} = opts;
        const time = this.rows.filter(row => [ShiftBatRowType.service].includes(row.type))?.[0]?.getTime() || 0;
        if (asDayJs) {
            return secsSinceMidnightToDayjs(time, date);
        }
        return time;
    }

    getActualEndTime(opts = {date: dayjs(), asDayJs: false}) {
        const {date, asDayJs} = opts;
        const lastServiceRow = last(this.rows.filter(row => [ShiftBatRowType.service].includes(row.type)));
        const time = lastServiceRow?.getEndTime() || 0 | lastServiceRow?.duration || 0;
        if (asDayJs) {
            return secsSinceMidnightToDayjs(time, date);
        }
        return time;
    }

    getActualStartLocation() {
        return this.rows.filter(row => [ShiftBatRowType.service].includes(row.type))?.[0]?.getEndLocation();
    }

    getActualEndLocation() {
        return last(this.rows.filter(row => [ShiftBatRowType.service].includes(row.type)))?.getEndLocation();
    }

    isRunning(date = dayjs(), schedulesById) {
        return (this.dates || []).some(d => d.isSame(date, 'day')) || this.scheduleIds.some(sId => {
            const schedule = schedulesById[sId];
            return schedule && schedule.isActive(date);
        });
    }

    getRunningDates(schedulesById, sameOrAfter = dayjs(), maxDays) {
        const dates = this.scheduleIds.reduce((p, c) => {
            c = schedulesById[c];
            if (!c) {
                return p;
            }
            return p.concat(c.getRunningDates(sameOrAfter));
        }, []).concat(this.dates || [])
        if(maxDays) {
            return dates.slice(0, maxDays);
        }
        return dates;
    }

    getOperatingDays(schedulesById) {
        const operatingDaysForSchedules = this.scheduleIds.reduce((p, c) => {
            c = schedulesById[c];
            if (!c) {
                return p;
            }
            p.push(c.operatingDays.toArray());
            return p;
        }, []);
        return OperatingDays.from(merge([], ...operatingDaysForSchedules));
    }

    getNextRunDate(date = dayjs(), schedulesById) {
        return this.rows.filter(r => r.type === ShiftBatRowType.service)?.[0]?.route?.getNextRunDate(date, schedulesById);
    }

    /**
     * Returns all variations of deadruns between this job and otherJob.
     * Deadrun from this job's end location to otherJob's start location
     * Deadrun from this job's actual end location to otherJob's start location
     * Deadrun from this job's end location to otherJob's actual start location
     * Deadrun from this job's actual end location to otherJob's actual start location
     * @param otherDuty
     */
    async getPossibleDeadruns(otherDuty) {
        if (!otherDuty) {
            throw new Error('Missing other duty');
        }
        let endToStart = await getDeadrun(this.getEndLocation(), otherDuty.getStartLocation(), otherDuty.getStartTime({asDayJs: true}).diff(this.getEndTime({asDayJs: true}), 's'));
        endToStart = combineDeadruns([this.getTrailingDeadrun(), endToStart, otherDuty.getLeadingDeadrun()]);
        let endToActualStart = await getDeadrun(this.getEndLocation(), otherDuty.getActualEndLocation(), otherDuty.getActualStartTime({asDayJs: true}).diff(this.getEndTime({asDayJs: true}), 's'));
        endToActualStart = combineDeadruns([this.getTrailingDeadrun(), endToActualStart]);
        let actualEndToStart = await getDeadrun(this.getActualEndLocation(), otherDuty.getStartLocation(), otherDuty.getStartTime({asDayJs: true}).diff(this.getEndTime({asDayJs: true}), 's'));
        actualEndToStart = combineDeadruns([actualEndToStart, otherDuty.getLeadingDeadrun()]);
        let actualEndToActualStart = await getDeadrun(this.getActualEndLocation(), otherDuty.getActualStartLocation(), otherDuty.getActualStartTime({asDayJs: true}).diff(this.getActualEndTime({asDayJs: true}), 's'));
        return {
            endToStart, endToActualStart, actualEndToStart, actualEndToActualStart,
        };
    }

    async getBestDeadrun(otherDuty, untouchedDeadrun, deadrunSortFn) {
        const deadruns = await this.getPossibleDeadruns(otherDuty);
        return calculateBestDeadrun(Object.values(deadruns).filter(deadrun => !!deadrun).concat(untouchedDeadrun), deadrunSortFn);
    }

    getShiftTime() {
        const excludedBreakTime = this.rows
            .filter(row => [ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken]
                .includes(row.type))
            .reduce((p, c) => p + (c.duration || 0), 0);

        let endRowTime = this.rows.reduce((p, c) => {
            const rowEndTime = (c.getEndTime() || 0)
            if(!c.hideTime && rowEndTime > p) {
                return rowEndTime
            }
            return p;
        }, 0);
        let startRowTime = this.rows.reduce((p, c) => {
            const rowTime = (c.getTime() || 0)
            if(!c.hideTime && rowTime > 0 && rowTime < p) {
                return rowTime
            }
            return p;
        }, Number.MAX_SAFE_INTEGER);
        const result = endRowTime - startRowTime - excludedBreakTime;
        return result > 0 ? result : 0;
    }

    isSchoolServiceOnly() {
        return this.rows.filter(row => row.type === ShiftBatRowType.service).every(row => row.route?.routeType === 'School');
    }

    isLinkedTo(otherShiftBat) {
        if (this.isSchoolServiceOnly() !== otherShiftBat.isSchoolServiceOnly()) {
            return false;
        }
        const otherShiftBatNumber = toNumber(otherShiftBat.shiftBatNumber);
        const dutyNumber = toNumber(this.shiftBatNumber);
        return otherShiftBatNumber === dutyNumber;
    }

    containsCharter() {
        return this.rows.filter(r => r.type === ShiftBatRowType.service).some(row => row.charter);
    }

    getShiftDistance() {
        return this.rows.reduce((p, c) => p + c.getDistance?.() || 0, 0);
    }

    getTotalRestTime(minimumRestTime) {
        return this.rows.filter(r => [ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken].includes(r.type)).reduce((p, c) => {
            const restTime = c.getDuration?.() || 0;
            if (restTime < minimumRestTime) {
                return p;
            }
            return p + restTime;
        }, 0);
    }

    getMaxConsecutiveRestTime() {
        const restTimes = this.rows.filter(r => r.type === ShiftBatRowType.break).map(r => r.duration);
        return Math.max(...restTimes, 0);
    }

    static createRow(row, modelData) {
        switch (row.type) {
            case ShiftBatRowType.service:
                row = new ShiftBatServiceRow(row);
                break;
            case ShiftBatRowType.charter:
                row = new ShiftBatCharterRow(row);
                break;
            case ShiftBatRowType.stop:
                row = new ShiftBatStopRow(row);
                break;
            case ShiftBatRowType.break:
            case ShiftBatRowType.breakMeal:
            case ShiftBatRowType.breakBroken:
                row = new ShiftBatBreakRow(row);
                break;
            case ShiftBatRowType.dead:
                row = new ShiftBatDeadRow(row);
                break;
            case ShiftBatRowType.note:
            case ShiftBatRowType.noteTimed:
                row = new ShiftBatNoteRow(row);
                break;
            case ShiftBatRowType.template:
                row = new ShiftBatTemplateRow(row, modelData);
                break;
            default:
                row = new ShiftBatInvalidRow(row);
        }
        return row;
    }

    createRow(row, modelData) {
        const newRow = ShiftBat.createRow(row, modelData);
        newRow.shiftBat = this;
        return newRow;
    }

    updateAllRows() {
        this.rows.forEach(row => row.loaded = false);
    }


    async updateRows({apiKey, allStops, allTransfers, allRoutes, allDeadruns, deadrunModelData, opts = {directions: true}}) {

        // eslint-disable-next-line
        let prevRow, nextRow;
        for (let i = 0; i < this.rows.length; i++) {
            if (i > 0) {
                // eslint-disable-next-line
                prevRow = this.rows[i - 1];
            }
            let row = this.rows[i];
            if (i < this.rows.length - 1) {
                // eslint-disable-next-line
                nextRow = this.rows[i + 1];
            }

            if (i > 0 && [ShiftBatRowType.service, ShiftBatRowType.stop].includes(row.type)) {
                if (this.checkPrevLoc(row, i)) {
                    i++;
                }
            }

        }

        
        try {
            // Ensure all rows are updated before deadrun rows.
            const nonDeadRows = this.rows.filter(row => row.type !== ShiftBatRowType.dead && row.updateRow);
            
            // Process non-dead rows sequentially to avoid race conditions
            for (const row of nonDeadRows) {
                try {
                    await row.updateRow({allStops, allTransfers, allRoutes});
                } catch (error) {
                    console.error(`[${dayjs().format('HH:mm:ss')} updateRows ${this.shiftBatId}] Error updating non-dead row ${row.id} for shift ${this.shiftBatNumber}:`, error);
                    // Continue with other rows even if one fails
                }
            }
            
            // Update deadrun rows last as they may need to get the points from their adjacent rows.
            const deadRows = this.rows.filter(row => row.type === ShiftBatRowType.dead && row.updateRow);
            
            // Process dead rows sequentially
            for (const row of deadRows) {
                try {
                    await row.updateRow({allDeadruns, allStops, allTransfers, allRoutes, ...opts});
                } catch (error) {
                    console.error(`[${dayjs().format('HH:mm:ss')} updateRows ${this.shiftBatId}] Error updating dead row ${row.id} for shift ${this.shiftBatNumber}:`, error);
                    // Continue with other rows even if one fails
                }
            }
            
        } catch (error) {
            console.error(`[${dayjs().format('HH:mm:ss')} updateRows ${this.shiftBatId}] Error in updateRows for shift ${this.shiftBatNumber}:`, error);
            throw error;
        }

        this.distance = this.getShiftDistance();
        this.duration = this.getShiftTime();
        this.signOn = this.getStartTime();
        this.signOff = this.getEndTime();

        return this;
    }

    addRow(row, idx = 0) {
        return this.putRow(idx, row);
    }

    deleteNextDeadRow(idx) {
        for (let i = idx + 1; i < this.rows.length; i++) {
            const row = this.rows[i];
            if ([ShiftBatRowType.service, ShiftBatRowType.stop].includes(row.type)) {
                return;
            }
            if (row.type === ShiftBatRowType.dead) {
                this.rows.splice(i, 1);
                return;
            }
        }
    }

    deletePrevDeadRow(idx) {
        for (let i = idx - 1; i >= 0; i--) {
            const row = this.rows[i];
            if ([ShiftBatRowType.service, ShiftBatRowType.stop].includes(row.type)) {
                return;
            }
            if (row.type === ShiftBatRowType.dead) {
                this.rows.splice(i, 1);
                return;
            }
        }
    }

    replaceRow(row, idx = 0) {
        let replace = false;
        let existingIdx = this.rows.findIndex(r => row.id === r.id);
        if (existingIdx > -1) {
            replace = true;
            idx = existingIdx;
        }
        // const currentRow = this.rows[idx]
        row = this.putRow(idx, row, replace);
        // Check for dead runs on either side. Delete so they will be rebuilt.
        // if (row && [ShiftBatRowType.service, ShiftBatRowType.stop].includes(row.type)) {// && row.locationUpdated(currentRow)) {
            this.deleteNextDeadRow(idx);
            this.deletePrevDeadRow(idx);
            // let prevRow, nextRow;
            // if (idx > 0) {
            //     prevRow = this.rows[idx - 1];
            // }
            // if (idx < this.rows.length - 1) {
            //     nextRow = this.rows[idx + 1];
            // }
            //
            // if (nextRow?.type === ShiftBatRowType.dead && !nextRow.routeEdited) {
            //     this.rows.splice(idx + 1, 1);
            // }
            // if (prevRow?.type === ShiftBatRowType.dead && !prevRow.routeEdited) {
            //     this.rows.splice(idx - 1, 1);
            // }
            // } else if (row.type === ShiftBatRowType.dead) {
            //     this.rows[idx + 1].time = -1;
        // }
        return row;
    }

    putRow(idx, row, replace) {
        row = this.createRow(row);
        const existingRow = this.rows[idx];
        if (isEqual(row, existingRow)) {
            return existingRow;
        }
        this.rows.splice(idx, replace ? 1 : 0, row);

        this.setTimeAuto(row, idx);
        // if (existingRow && existingRow.time > -1isValid && row.time > -1 && row.time !== existingRow.time) {
        //     row.manualTime = true;
        // }

        return row;
    }

    setTimeAuto(row, idx) {
        if (idx > 0 && (!Number.isFinite(row.time) || row.time < 0) && ![ShiftBatRowType.service, ShiftBatRowType.note, ShiftBatRowType.dead].includes(row.type)) {
            let prevRow = this.rows[idx - 1];
            if (prevRow?.getEndTime) {
                row.time = prevRow.getEndTime();
            }
        } else if([ShiftBatRowType.service, ShiftBatRowType.note, ShiftBatRowType.dead].includes(row.type)) {
            row.time = -1;
        }
    }

    removeRow(row) {
        const idx = this.rows.findIndex(r => row.id === r.id);
        if (idx >= 0) {
            if ([ShiftBatRowType.service, ShiftBatRowType.stop, ShiftBatRowType.noteTimed].includes(row.type)) {
                this.deleteNextDeadRow(idx);
            }
            this.rows.splice(idx, 1);
            if ([ShiftBatRowType.service, ShiftBatRowType.stop, ShiftBatRowType.noteTimed].includes(row.type)) {
                this.deletePrevDeadRow(idx);
            }
        }
    }

    checkPrevLoc(row, idx) {
        let addedDeadRow = false;
        if (idx > 0 && row.type !== ShiftBatRowType.dead) {
            let prevIdx = idx - 1;
            let prevRow = this.rows[prevIdx--];

            if (prevRow.type === ShiftBatRowType.dead) {
                return false;
            }

            // Add dead running if necessary
            if (row.getStartLocation) {
                const toLoc = row.getStartLocation();
                if (toLoc) {
                    let prevLocRow = prevRow,
                        prevTimeRow = Number.isFinite(prevRow.time) && prevRow.time > -1 ? prevRow : null;
                    while (prevIdx >= 0 && !prevLocRow.getEndLocation) {
                        prevLocRow = this.rows[prevIdx--];
                        if (!prevTimeRow && Number.isFinite(prevLocRow.time) && prevLocRow.time > -1) {
                            prevTimeRow = prevLocRow;
                        }
                    }

                    if (prevLocRow.getEndLocation) {
                        const fromLoc = prevLocRow.getEndLocation();
                        if (fromLoc && toLoc.geohash !== fromLoc.geohash) {
                            const distance = getDistanceInMetres(prevLocRow.getEndLocation(), row.getStartLocation());
                            const duration = distance / 14;

                            const deadRow = this.createRow({
                                type: ShiftBatRowType.dead,
                                time: prevTimeRow ? prevTimeRow.getEndTime() : -1,
                                start: prevLocRow.getEndLocation(),
                                startStopId: prevLocRow.getEndLocation().stopId,
                                end: row.getStartLocation(),
                                endStopId: row.getStartLocation().stopId,
                                distance,
                                duration,
                            });
                            this.rows.splice(idx, 0, deadRow);
                            addedDeadRow = true;
                            prevRow = deadRow;
                        }
                    }
                }
            }

            // Update the new rows time
            // if (row.time === -1 && ![ShiftBatRowType.service].includes(row.type)) {
            //     row.time = prevRow.time + (prevRow.duration || 0)
            // }
            row.prevRow = prevRow;
        }
        return addedDeadRow;
    }

    /**
     * ShiftBat to TimeBlocks
     * A work TimeBlock is made up of rows that are not of type breakMeal, breakBroken
     * A break TimeBlock is made up of rows that are of type breakMeal, breakBroken
     */
    toTimeBlocks(id) {
        const timeBlocks = [];
        let startTime = this.getStartTime();
        let endTime = this.getEndTime();
        this.rows.forEach(row => {
            if ([ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken, ShiftBatRowType.break].includes(row.type)) {
                endTime = row.getTime();
                if (endTime > startTime) {
                    timeBlocks.push(new TimeBlock({id, type: 'work', startTime, endTime}));
                }
                timeBlocks.push(new TimeBlock({
                    id,
                    type: 'break',
                    startTime: row.getTime(),
                    endTime: row.getEndTime()
                }));
                startTime = row.getEndTime();
                endTime = this.getEndTime();
            }
        });
        if (endTime > startTime) {
            timeBlocks.push(new TimeBlock({id, type: 'work', startTime, endTime}));
        }

        return timeBlocks;
    }

    getStopTimes() {
        let stopTimes = [];
        let prevStopDistance = 0;
        
        // First pass: collect all stops and their base distances, including deadrun distances
        this.rows.sort((a, b) => a.getTime() - b.getTime()).forEach((row, idx) => {
            const prevRow = idx > 0 ? this.rows[idx - 1] : null;

            let currentDeadrunDistance = 0;

            // Store any deadrun distance before the current row
            if (prevRow?.type === ShiftBatRowType.dead) {
                currentDeadrunDistance = prevRow.getDistance();
            }

            if (row.type === ShiftBatRowType.service && row.trip) {
                // Add all stops from the trip with their base distances
                const serviceStops = row.trip.stops.map((st) => ({
                    ...st,
                    distance: (st.distance || 0) + prevStopDistance + currentDeadrunDistance
                }));
                stopTimes.push(...serviceStops);
                prevStopDistance = last(serviceStops).distance;
            } else if (row.type === ShiftBatRowType.stop) {
                stopTimes.push({
                    arriveSecs: row.getTime(),
                    departSecs: row.getEndTime(),
                    stopId: row.getStartLocation().stopId,
                    stopTimeId: ulid(),
                    stopName: row.getStartLocation().stopName,
                    lat: row.getStartLocation().lat,
                    lon: row.getStartLocation().lon,
                    distance: prevStopDistance + currentDeadrunDistance,
                });

                prevStopDistance = last(stopTimes).distance;
            }
        });

        return stopTimes;
    }

    getAllWaypoints() {
        return concat(this.rows.filter(row => row.route?.waypoints).map(row => row.route.waypoints));
    }

    toGeoJson() {
        const startTime = dayjs();
        const lineStrings = this.rows.filter(row => row.route?.waypoints).map(row => {
            const time = row.getTime();
            const route = row.route;
            if(row.type === ShiftBatRowType.dead) {
                route.routeNumber = 'SPECIAL';
            }
            return toLineString(route, startTime.add(time, 's'));
        });
        return {
            type: 'FeatureCollection',
            features: lineStrings
        };
    }

    toJson() {
        const excludedKeys = ['schedules', 'warning', 'warnings'];
        
        const jsonData = {
            ...this,
            forceUpdate: undefined,
            createdAt: this.createdAt.format(DATE_STRING),
            rows: this.rows.map(row => row.toJson()),
            effectiveDate: this.effectiveDate?.format(DATE_STRING),
            dates: this.dates?.map(date => date.format(DATE_STRING)),
        };
        
        return pickBy(jsonData, (value, key) => {
            const hasValue = value !== null && value !== undefined;
            const isAllowedKey = !excludedKeys.includes(key);
            return hasValue && isAllowedKey;
        });
    }

    getRowValidation(rowIndex, options = {}) {
        if (rowIndex < 0 || rowIndex >= this.rows.length) {
            return null;
        }
        const row = this.rows[rowIndex];
        row.shiftBat = this; // Ensure the row has reference to this shiftBat
        return getRowValidation(row, rowIndex, options);
    }
}

export const ShiftBatRowType = {
    noteTimed: 'Note timed',
    note: 'Note',
    service: 'Service',
    'break': 'Break',
    breakMeal: 'Meal break',
    breakBroken: 'Broken shift break',
    location: 'Add location',
    stop: 'Point',
    stopNote: 'Stop Note',
    dead: 'Dead running',
    charter: 'Charter route',
    template: 'Template'

};
export const ShiftBatRowTypeReverseMap = {
    'Note timed': 'noteTimed',
    'Note': 'note',
    'Service': 'service',
    'Break': 'break',
    'Meal break': 'breakMeal',
    'Broken shift break': 'breakBroken',
    'Add location': 'location',
    'Point': 'stop',
    'Stop Note': 'stopNote',
    'Dead running': 'dead',
    'Charter route': 'charter',
    'Template': 'template'
};

export const ShiftBatRowTypeLabel = {
    itinerary: 'Itinerary',
    service: 'Service',
    stop: 'Point',
    noteTimed: 'Note - Timed',
    note: 'Note - Untimed',
    'break': 'Break - Crib',
    breakMeal: 'Break - Meal',
    breakBroken: 'Break - Split Shift',
    location: 'Add location',
    stopNote: 'Stop Note',
    dead: 'Dead running',
    charter: 'Charter route',
    template: 'Template'
};
export const SelectableShiftBatRowTypes = ['service', 'stop', 'noteTimed', 'note', 'break', 'breakMeal', 'breakBroken'];
export const ShiftBatRowNotePriority = {normal: 'Normal', high: 'High'};
export const ShiftBatRowNotePriorityLabel = {normal: 'Normal', high: 'Important'};

export const abbreviate = text => {
    if(!text) return '';
    const abbreviations = {
        'Street': 'St',
        'Avenue': 'Ave',
        'Boulevard': 'Blvd',
        'Drive': 'Dr',
        'Esplanade': 'Esp',
        'Road': 'Rd',
        'Lane': 'Ln',
        'Court': 'Ct',
        'Square': 'Sq',
        'Trail': 'Trl',
        'Parkway': 'Pkwy',
        'Terrace': 'Tce',
        'Place': 'Pl',
        'Circle': 'Cir',
        'Highway': 'Hwy',
        'Crescent': 'Cr',
        'Parade': 'Pde',
        'Circuit': 'Cct',
        'Close': 'Cl'
    };

    // Create a regex pattern from the keys of the abbreviations object
    const pattern = new RegExp('\\b(' + Object.keys(abbreviations).join('|') + ')(?=,|$)', 'gi');

    // Replace matched patterns with their abbreviations
    return text.replace(pattern, (match) => abbreviations[match]);
};

export class ShiftBatRow {
    constructor(props) {

        this.id = ulid();
        this.type = ShiftBatRowType.noteTimed;
        this.title = '';
        this.description = '';
        this.priority = ShiftBatRowNotePriority.normal;
        this.time = -1; //seconds since midnight
        this.loaded = false;
        this.loading = false;
        this.initialised = false;
        this.shiftBat = null;
        Object.assign(this, props);

        if (this.title) {
            this.title = abbreviate(this.title);
        }

        if (this.description) {
            this.description = abbreviate(this.description);
        }
    }

    reset() {
        this.time = -1;
        this.duration = 0;
        this.distance = 0;
        this.route = null;
        this.trip = null;
        this.stop = null;
        this.title = '';
        this.description = '';
        this.priority = ShiftBatRowNotePriority.normal;
        return this.shiftBat.createRow(this);
    }

    cloneDeep() {
        const shiftBat = this.shiftBat;
        delete this.shiftBat;
        return shiftBat.createRow(cloneDeep(this));
    }

    clone() {
        const shiftBat = this.shiftBat;
        if (!shiftBat) {
            throw new Error('Phantom row! ShiftBatRow.clone: shiftBat is not set');
        }
        return shiftBat.createRow(clone(this));
    }

    // Validation helpers
    isLoadedBypass() {
        return !this.loaded;
    }

    isValidTitle() {
        return !!this.title?.length;
    }

    isValidTime() {
        if (this.hideTime) {
            return true;
        }
        return Number.isFinite(this.time) && this.time >= 0;
    }

    isValidSequence(i) {
        if (!Number.isFinite(i) || i <= 0) {
            return true;
        }
        const prevEndTime = this.shiftBat?.rows?.[i - 1]?.getEndTime?.();
        return (this.getTime() - prevEndTime) >= -60;
    }

    isValid(args, i, excludedFields = []) {
        console.log('ShiftBatRow isValid: ', this.title);
        if (this.isLoadedBypass()) {
            console.log('ShiftBatRow VALID: not loaded');
            return true;
        }
        if (!this.isValidTitle()) {
            console.log('ShiftBatRow INVALID: no title');
            return false;
        }
        if (!excludedFields.includes('time') && !this.isValidTime()) {
            console.log('ShiftBatRow INVALID: invalid time');
            return false;
        }
        if (!this.isValidSequence(i)) {
            console.log('ShiftBatRow INVALID: time is before the end of ' + this.shiftBat.rows[i - 1].title);
            console.log('ShiftBatRow INVALID: time: ', this.getTime());
            console.log('ShiftBatRow INVALID: endTime: ', this.shiftBat.rows[i - 1].getEndTime());
            return false;
        }
        console.log('ShiftBatRow VALID');
        return true;
    }

    invalidReason(args, i, excludedFields = []) {
        if (!this.title?.length) {
            return 'Missing title';
        }
        if (!excludedFields.includes('time') && this.time < 0) {
            return 'Invalid time';
        }
        if (i > 0 && this.getTime() < this.shiftBat.rows[i - 1].getEndTime()) {
            return 'Time is before the end of ' + this.shiftBat.rows[i - 1].title;
        }
    }

    getInvalidMessage() {
        const missing = [];
        if (!this.title?.length) missing.push('title');
        if (this.time < 0) missing.push('time');
        return missing?.length ? `Missing ${missing.join(', ')}` : null;
    }

    isDefault() {
        return !this.title?.length && !this.description?.length && (!this.priority?.length || this.priority === ShiftBatRowNotePriority.normal);
    }

    needsUpdate() {
        return false;
    }

    getTime() {
        if(!this.shiftBat) {
            return 0;
        }
        if(this.hideTime) {
            const currentIdx = this.shiftBat.rows.findIndex(row => row.id === this.id);
            // Find the previous row with a valid endTime
            for (let i = currentIdx - 1; i >= 0; i--) {
                const prevRow = this.shiftBat.rows[i];
                if (prevRow && !prevRow.hideTime && prevRow.getEndTime() >= 0) {
                    return prevRow.getEndTime();
                }
            }
            return 0;
        }
        if (Number.isFinite(this.time) && this.time >= 0) {
            return Math.floor(this.time / 60) * 60
        }
    }

    getEndTime() {
        if(this.hideTime) {
            const currentIdx = this.shiftBat.rows.findIndex(row => row.id === this.id);
            // Find the next row with a valid time
            const rows = this.shiftBat.rows;
            for (let i = currentIdx + 1; i < rows.length; i++) {
                const nextRow = rows[i];
                if (nextRow && !nextRow.hideTime && nextRow.getTime() >= 0) {
                    return nextRow.getTime();
                }
            }
            return -1;
        }
        if (Number.isFinite(this.getTime()) && this.getTime() >= 0) {
            // calculate duration to the nearest minute using ceiling
            const duration = this.getDuration() > 0 ? Math.ceil(this.getDuration() / 60) * 60 : 0;
            return Math.ceil((this.getTime() + duration) / 60) * 60;
        }
        return 0;
    }

    toTimeString() {
        const time = this.getTime();
        if (Number.isFinite(time) && time >= 0) {
            return toTime(time);
        }
        return '';
    }

    getDuration() {
        return this.duration || 0;
    }

    getDistance() {
        return this.distance || 0;
    }

    toJson() {
        const excludedKeys = [
            'prevRow', 'loaded', 'notes', 'routes', 
            'shiftBat', 'warning', 'warnings'
        ];
        
        return pickBy({...this}, (value, key) => {
            const hasValue = value !== null && value !== undefined;
            const isAllowedKey = !excludedKeys.includes(key);
            return hasValue && isAllowedKey;
        });
    }
    async updateRow(data = {}) {

    }
}

export class ShiftBatLocationRow extends ShiftBatRow {

    getStartLocation() {
        throw new Error('Please implement getStartLocation');
    }

    getEndLocation() {
        throw new Error('Please implement getEndLocation');
    }

    locationUpdated(otherRow) {
        throw new Error('Please implement locationUpdated');

    }
}

export class ShiftBatServiceRow extends ShiftBatLocationRow {
    constructor(props) {
        super(props);
        this.type = ShiftBatRowType.service;
        this.route = null;
        this.startStopIdx = -1;
        this.endStopIdx = -1;
        this.startStopTimeId = null;
        this.endStopTimeId = null;
        this.trip = null;
        this.stopRows = null;
        this.duration = 0;
        this.distance = 0;
        this.hasDirections = false;
        Object.assign(this, props);

        if (this.route) {
            if (this.startStopIdx > -1) {
                this.route.startStopIdx = this.startStopIdx;
            }
            if (this.endStopIdx > -1) {
                this.route.endStopIdx = this.endStopIdx;
            }
            this.route = new BusRoute(this.route);
        }
        if (this.trip) {
            this.trip = new Trip(this.trip);
        }

        if (this.stopRows) {
            this.stopRows = this.stopRows.map(sr => new ShiftBatStopRow(sr));
        }

        // Set time to -1 as it is dynamically set in updateRow
        this.time = -1;

        // if (this.trip && this.route?.stops) {
        //     const stopRows = this.route?.stops.map((stop, idx) => new ShiftBatStopRow({
        //         stop: stop,
        //         time: getDepartureTimeAsSecondsSinceMidnight(this.trip, this.trip.stopTimes[idx]),
        //         title: stop.stopName,
        //         sequence: idx + 1
        //     }));
        //
        //     if(this.stopRows) {
        //         stopRows.forEach((sr, idx) => {
        //             const existingSr = this.stopRows[idx]
        //             if (existingSr?.stop.stopId === sr.stop.stopId) {
        //                 sr.noteRows = this.stopRows[idx].noteRows
        //                 sr.transfersTo = this.stopRows[idx].transfersTo
        //                 sr.transfersFrom = this.stopRows[idx].transfersFrom
        //             }
        //         })
        //     }
        //
        //     // if (stopRows?.length === this.stopRows?.length &&
        //     //     differenceWith(stopRows, this.stopRows,
        //     //         (a, b) => a.stop?.stopTimeId === b.stop?.stopTimeId && a.stop?.stopId === b.stop?.stopId).length === 0) {
        //     //     stopRows.forEach((sr, idx) => {
        //     //         sr.noteRows = this.stopRows[idx].noteRows
        //     //         sr.transfersTo = this.stopRows[idx].transfersTo
        //     //         sr.transfersFrom = this.stopRows[idx].transfersFrom
        //     //     })
        //     // }
        //     this.stopRows = stopRows;
        //     if (this.stopRows?.length) {
        //         this.duration = last(this.stopRows).time - this.stopRows[0].time;
        //     }
        // }

    }

    isSplitShift() {
        return this.startStopTimeId || this.endStopTimeId;
    }
    isDeprecatedSplitShift() {
        return !this.startStopTimeId && !this.endStopTimeId && (this.startStopIdx > -1 || this.endStopIdx > -1);
    }

    // Validation helpers
    isValidRoute({allRoutes}) {
        console.log('isValidRoute: ', this.routeId, this.route)
        const route = this.route || allRoutes?.[this.routeId];
        return !!route;
    }

    isValidTrip({allRoutes}) {
        console.log('isValidTrip: ', this.tripId, this.trip)
        const route = this.route || allRoutes?.[this.routeId];
        if (!route) return false;
        const trip = find(route.services, {tripId: (this.trip?.tripId || this.tripId)});
        if(!trip) return false;
        return true;
    }

    isValidTripSchedule(trip) {
        if(!trip) return false;
        if(this.shiftBat?.charter) return true;
        const hasNoSchedulesOrDates = !this.shiftBat.scheduleIds?.length && !this.shiftBat.dates?.length;

        const allScheduleIdsMissing = this.shiftBat.scheduleIds.every(
            sId => !trip.scheduleIds.includes(sId)
        );

        const allDatesMissing = this.shiftBat.dates.every(date => {
            // trip.date may be a single date, trip.dates may be an array
            const tripDateMismatch = !trip.date?.isSame?.(date, 'day');
            const allTripDatesMismatch = trip.dates?.every?.(date2 => !date.isSame(date2, 'day'));
            return tripDateMismatch && allTripDatesMismatch;
        });

        if (hasNoSchedulesOrDates || (allScheduleIdsMissing && allDatesMissing)) {
            return false;
        }
        return true;
    }
    
    isValidSplitStops({allRoutes}) {
        // If no split stops are set, it's valid
        if (!this.startStopTimeId && !this.endStopTimeId) {
            return true;
        }
        
        const route = this.route || allRoutes?.[this.routeId];
        if (!route) return true; // Will be caught by isValidRoute
        
        const trip = this.trip || find(route.services, {tripId: (this.trip?.tripId || this.tripId)});
        if (!trip) return true; // Will be caught by isValidTrip
        
        // Check if startStopTimeId exists in trip
        if (this.startStopTimeId) {
            const startStopExists = trip.stopTimes?.some(st => st.stopTimeId === this.startStopTimeId);
            if (!startStopExists) {
                return false;
            }
        }
        
        // Check if endStopTimeId exists in trip
        if (this.endStopTimeId) {
            const endStopExists = trip.stopTimes?.some(st => st.stopTimeId === this.endStopTimeId);
            if (!endStopExists) {
                return false;
            }
        }
        
        return true;
    }

    isValidSequence(i) {
        if (!Number.isFinite(i) || i <= 0) {
            return true;
        }
        const prevEndTime = this.shiftBat?.rows?.[i - 1]?.getEndTime?.();
        return (this.getEndTime() - prevEndTime) >= -60;
    }

    isValid({allRoutes}, i) {
        if (!this.loaded) {
            return true;
        }
        if (!this.isValidRoute({allRoutes})) {
            return false;
        }
        if (!this.isValidTrip({allRoutes})) {
            return false;
        }
        if (!this.isValidSplitStops({allRoutes})) {
            return false;
        }
        return super.isValid({allRoutes}, i, ['time']);
    }

    invalidReason({allRoutes}, i) {

        const route = allRoutes[this.routeId || this.route?.routeId];
        if (!route) {
            return `The ${this.title} route has been deleted, moved to Draft status, or is invalid`;
        }
        const trip = find(route.services, {tripId: (this.tripId || this.trip?.tripId)});
        if (!trip) {
            return `The ${getSecondsSinceMidnightAsDayjs(this.time).format('HH:MM')} service for ${allRoutes[this.routeId || this.route?.routeId].routeNumber} has been deleted, moved to Draft status, or is invalid`;
        }
        if (!this.isValidTripSchedule(trip)) {
            return `The ${this.title} service is not scheduled for this shift`;
        }
        
        // Check for invalid split stops
        if (!this.isValidSplitStops({allRoutes})) {
            const invalidStops = [];
            if (this.startStopTimeId && !trip.stopTimes?.some(st => st.stopTimeId === this.startStopTimeId)) {
                invalidStops.push('start stop');
            }
            if (this.endStopTimeId && !trip.stopTimes?.some(st => st.stopTimeId === this.endStopTimeId)) {
                invalidStops.push('end stop');
            }
            return `The ${this.title} service has invalid split stops: ${invalidStops.join(' and ')} no longer exist in this trip. Please reset the split.`;
        }
        
        return super.invalidReason({allRoutes}, i, ['time']);
    }

    getInvalidMessage() {
        const missing = [];
        if (!this.route) missing.push('route');
        if (!this.trip) missing.push('trip');
        if (this.time < 0) missing.push('time');
        return missing?.length ? `Missing ${missing.join(', ')}` : null;
    }

    isDefault() {
        return super.isDefault() && !this.route && !this.trip;
    }

    getTime() {
        return Math.floor((this.trip?.getStartTimeAsSecondsSinceMidnight(this.startStopIdx > 0 ? this.startStopIdx : undefined) || 0) / 60) * 60;
    }

    getEndTime() {
        return Math.ceil((this.trip?.getEndTimeAsSecondsSinceMidnight(this.endStopIdx > 0 ? this.endStopIdx : undefined) || 0) / 60) * 60;
    }

    needsUpdate() {
        return !this.loaded && !this.loading;
        // return !this.route || (!this.route.waypoints?.length || !this.stopRows?.length || !this.stopRows[0].transfersFrom || !this.stopRows[0].transfersTo || !this.distance || !this.duration)
    }

    async updateRow({apiKey, allStops, allTransfers = {}, allRoutes, opts = {directions: true}}) {
        if (!this.route && !this.routeId) {
            return;
        }
        this.loading = true;
        try {
            this.route = this.route || allRoutes[this.routeId]?.shallowClone();
            if (!this.route) {
                console.log('ROUTE CANNOT BE FOUND!!!');
                this.loaded = true;
                this.loading = false;
                return;
            }
            //Removed clone to speed up this function as I don't think it's necessary.. mainly due to the number of shifts in Busabout Wagga
            this.trip = this.trip || find(this.route.services, ['tripId', this.tripId]);
            this.route.setTransfers(allTransfers, allRoutes, true);

            if (this.startStopTimeId || this.endStopTimeId || this.startStopIdx > -1 || this.endStopIdx > -1) {
                // Recalculate indices from stable stopTimeId identifiers if available
                if (this.startStopTimeId) {
                    const foundIdx = this.trip.stopTimes.findIndex(st => st.stopTimeId === this.startStopTimeId);
                    if (foundIdx > -1) {
                        this.startStopIdx = foundIdx;
                    } else {
                        // Invalid stopTimeId - log warning but keep it for validation to catch
                        console.warn(`Invalid startStopTimeId ${this.startStopTimeId} for trip ${this.tripId}. Validation will flag this.`);
                        this.startStopIdx = -1; // Reset index but keep stopTimeId for validation
                    }
                }
                
                if (this.endStopTimeId) {
                    const foundIdx = this.trip.stopTimes.findIndex(st => st.stopTimeId === this.endStopTimeId);
                    if (foundIdx > -1) {
                        this.endStopIdx = foundIdx;
                    } else {
                        // Invalid stopTimeId - log warning but keep it for validation to catch
                        console.warn(`Invalid endStopTimeId ${this.endStopTimeId} for trip ${this.tripId}. Validation will flag this.`);
                        this.endStopIdx = -1; // Reset index but keep stopTimeId for validation
                    }
                }
                
                // Only apply split if we have valid indices
                if (this.startStopIdx > -1 || this.endStopIdx > -1) {
                    this.route.calculateStartEnd({
                        firstStop: this.route.stops[this.startStopIdx > -1 ? this.startStopIdx : 0],
                        lastStop: this.route.stops[this.endStopIdx > -1 ? this.endStopIdx : this.route.stops.length - 1]
                    });
                }
            }
            this.distance = this.route.getDistance();
            this.duration = this.route.getDuration(this.trip);

            if (this.stopRows) {
                this.stopRows = this.stopRows.map(sr => {
                    if (!sr.stop && sr.stopId) {
                        sr.stop = allStops[sr.stopId];
                    }
                    return new ShiftBatStopRow(sr);
                });
            }

            if (this.trip && this.route?.stops) {
                const stopRows = this.trip.stops
                    .filter((st, idx) => config?.operator?.opts?.shiftbat?.turnsOnAllStops || st.timingPoint || this.charter || this.route?.charter || this.shiftBat?.charter)
                    .map((st, idx) => {
                        return new ShiftBatStopRow({
                            stop: st,
                            stopTimeId: st.stopTimeId,
                            stopId: st.stopId,
                            tripId: this.trip.tripId,
                            routeId: this.route.routeId,
                            time: getDepartureTimeAsSecondsSinceMidnight(this.trip, st),
                            title: allStops[st.stopId]?.stopName || '',
                            sequence: this.trip.getStopSequence(st, allStops)
                        });
                    });

                if (this.stopRows) {
                    stopRows.forEach((sr, idx) => {
                        const existingSr = find(this.stopRows, {stopTimeId: sr.stopTimeId});
                        if (existingSr?.stop?.stopId === sr.stop.stopId || existingSr?.stopId === sr.stopId) {
                            sr.noteRows = existingSr.noteRows;
                            sr.transfersTo = existingSr.transfersTo;
                            sr.transfersFrom = existingSr.transfersFrom;
                        }
                    });
                }

                // if (stopRows?.length === this.stopRows?.length &&
                //     differenceWith(stopRows, this.stopRows,
                //         (a, b) => a.stop?.stopTimeId === b.stop?.stopTimeId && a.stop?.stopId === b.stop?.stopId).length === 0) {
                //     stopRows.forEach((sr, idx) => {
                //         sr.noteRows = this.stopRows[idx].noteRows
                //         sr.transfersTo = this.stopRows[idx].transfersTo
                //         sr.transfersFrom = this.stopRows[idx].transfersFrom
                //     })
                // }
                this.stopRows = stopRows;
                if (this.stopRows?.length) {
                    this.duration = last(this.stopRows).time - this.stopRows[0].time;
                }


                if (this.trip) {
                    this.stopRows.forEach(stopRow => {
                        stopRow.stop = stopRow.stop || allStops[stopRow.stopId];
                        stopRow.title = stopRow.title || stopRow.stop.stopName;
                        stopRow.transfersTo = this.trip.transfersTo?.filter(tx => tx.fromStopTimeId === stopRow.stop.stopTimeId)
                            .map(tx => ({...allRoutes[tx.toRouteId], txTripId: tx.toTripId})).filter(tx => !!tx);

                        stopRow.transfersFrom = this.trip.transfersFrom?.filter(tx => tx.toStopTimeId === stopRow.stop.stopTimeId)
                            .map(tx => ({...allRoutes[tx.fromRouteId], txTripId: tx.fromTripId})).filter(tx => !!tx);
                    });
                }
            }
            this.title = this.route.routeName;
            if (opts.directions) {
                await this.buildDirections();
            }
        } catch (e) {
            console.log(e, e);
        }
        this.loaded = true;
        this.loading = false;
    }

    getStopRows() {
        const startStopTimeId = this.trip?.stopTimes[this.startStopIdx > -1 ? this.startStopIdx : 0]?.stopTimeId;
        const endStopTimeId = this.trip?.stopTimes[this.endStopIdx > -1 ? this.endStopIdx : this.trip.stopTimes.length - 1]?.stopTimeId;
        const sRows = this.stopRows || [];
        const startIdx = sRows.findIndex(sr => sr.stopTimeId === startStopTimeId);
        const endIdx = sRows.findIndex(sr => sr.stopTimeId === endStopTimeId);
        return sRows.slice(startIdx, (endIdx === -1 ? sRows.length : endIdx) + 1);
    }

    addNote(stopSequence, note, priority) {
        this.stopRows[stopSequence - 1].addNote(new ShiftBatRow({title: note, priority}));
    }

    async buildDirections(force) {
        if (this.route?.stops?.length && this.trip?.stops?.length && (force || !this.hasDirections)) {
            this.stopRows.forEach(sr => sr.noteRows = sr.noteRows.filter(nr => !nr.directions));
            const stops = this.route.stops
                .filter((st, idx) => config?.operator?.opts?.shiftbat?.turnsOnAllStops || this.trip.stopTimes[idx]?.timingPoint || this.charter || this.route?.charter || this.shiftBat?.charter);
            const wpsBetweenStops = stops
                .slice(1).map((stop, idx) => {
                    const prevStopWpIdx = stops[idx].wpIdx;
                    const thisStopWpIdx = stop.wpIdx;
                    let wps = this.route.waypoints.slice(prevStopWpIdx, thisStopWpIdx + 1);
                    return {idx, wps};
                });

            try {
                await Promise.all(wpsBetweenStops.map(async wpsBetweenStops => {
                    if (!wpsBetweenStops.wps?.length) {
                        return;
                    }
                    const chunks = chunk(wpsBetweenStops.wps, 1000);
                    await eachSeries(chunks, (wps, done) => {
                        if (!wps?.length || wps.length < 2) {
                            return done();
                        }
                        getCachedDirections(wps).then(data => {
                            if (!data) {
                                return done();
                            }
                            const {directions, waypoints} = data;
                            if (directions?.length) {
                                this.stopRows[wpsBetweenStops.idx].noteRows.push(new ShiftBatRow({
                                    title: directions.join(', '), directions: true
                                }));
                            }

                            this.stopRows[wpsBetweenStops.idx].waypoints = this.stopRows[wpsBetweenStops.idx].waypoints || [];
                            this.stopRows[wpsBetweenStops.idx].waypoints.push(...waypoints.map(wp => ({
                                lat: wp.location[1], lon: wp.location[0]
                            })));
                            done();
                        }).catch(e => {
                            console.log(e, e);
                            done();
                        });
                    });
                }));
                // this.mappedRoute = new BusRoute({
                //     colour: '#FE73D1',
                //     waypoints: flatten(this.stopRows.filter(stopRow => stopRow.waypoints?.length).map(stopRow => stopRow.waypoints.filter(wp => wp.lat && wp.lon)))
                // })
                this.hasDirections = true;
            } catch (e) {
                console.log('Could not build directions: ' + e, e);
            }
        }
    }

    toJson() {
        const baseJson = super.toJson();
        const excludedKeys = [
            'mappedData', 'time', 'duration', 'distance', 
            'priority', 'shiftBat', 'startStopIdx', 'endStopIdx'
        ];
        
        const serviceData = {
            ...baseJson,
            routeId: this.route?.routeId || 'UNKNOWN',
            tripId: this.trip?.tripId || 'UNKNOWN',
            startStopTimeId: this.startStopTimeId,
            endStopTimeId: this.endStopTimeId,
            stopRows: this.stopRows.map(stopRow => stopRow.toJson())
        };
        
        return pickBy(serviceData, (value, key) => {
            if (value === null || value === undefined) {
                return false;
            }
            
            // Exclude trip if we have tripId
            if (key === 'trip' && (this.tripId || this.trip?.tripId)) {
                return false;
            }
            
            // Exclude route if we have routeId
            if (key === 'route' && (this.routeId || this.route?.routeId)) {
                return false;
            }
            
            return !excludedKeys.includes(key);
        });
    }

    getStartLocation() {
        return this.route?.stops?.length ? this.route.stops[this.startStopIdx >= 0 ? this.startStopIdx : 0] : null;
    }

    getEndLocation() {
        return this.route?.stops?.length ? this.route.stops[this.endStopIdx >= 0 ? this.endStopIdx : this.route.stops.length - 1] : null;
    }

    getDistance() {
        return this.route?.getDistance() || 0;
    }

    getDuration() {
        return this.route?.getDuration(this.trip) || 0;
    }

    locationUpdated(otherRow) {
        return otherRow?.route && this.route?.routeId !== otherRow.route?.routeId;
    }
}

export class ShiftBatCharterRow extends ShiftBatServiceRow {
    constructor(props) {
        super(props);
        this.type = ShiftBatRowType.charter;
    }
}

export class ShiftBatStopRow extends ShiftBatLocationRow {
    constructor(props) {
        super(props);
        // console.log("Creating ShiftBatStopRow")
        this.type = ShiftBatRowType.stop;
        this.stop = null;
        this.stopTimeId = null;
        this.stopId = null;
        this.tripId = null;
        this.routeId = null;
        this.sequence = 0;
        this.noteRows = [];
        this.transfersTo = [];
        this.transfersFrom = [];
        Object.assign(this, props);
        this.noteRows = this.noteRows.map(row => new ShiftBatRow(row));
        this.title = this.stop?.stopName;
    }

    addNote({id, note, priority}, idx) {
        console.log('Adding note...');
        this.noteRows.splice(idx, 0, new ShiftBatRow({
            id, title: note || '', priority: priority || ShiftBatRowNotePriority.normal
        }));
    }

    replaceNote(note) {
        const idx = this.noteRows.findIndex(n => n.id === note.id);
        if (idx > -1) {
            this.noteRows.splice(idx, 1, note);
        }
    }

    removeNote(row) {
        const idx = this.noteRows.findIndex(r => row.id === r.id);
        if (idx >= 0) {
            this.noteRows.splice(idx, 1);
        }
    }

    getStartLocation() {
        return this.stop;
    }

    getEndLocation() {
        return this.stop;
    }

    toJson() {
        const baseJson = super.toJson();
        const excludedKeys = [
            'duration', 'title', 'transfersFrom', 'transfersTo', 
            'priority', 'loaded', 'shiftBat'
        ];
        const excludedNoteKeys = ['description', 'time'];
        
        const processedNoteRows = this.noteRows.map(noteRow => {
            const noteJson = noteRow.toJson();
            return pickBy(noteJson, (value, key) => {
                return !excludedNoteKeys.includes(key);
            });
        });
        
        const stopData = {
            ...baseJson,
            stopId: this.stop?.stopId || 'UNKNOWN',
            noteRows: processedNoteRows
        };
        
        return pickBy(stopData, (value, key) => {
            if (value === null || value === undefined) {
                return false;
            }
            
            // Only include the stop object if there is no stopId
            if (key === 'stop') {
                return !value.stopId;
            }
            
            return !excludedKeys.includes(key);
        });
    }

    async updateRow({allStops = {}}) {
        if (this.stop) {
            this.title = this.stop?.stopName || '';
            this.stopId = this.stop?.stopId;
            this.description = this.description || '';
            this.loaded = true;
            return;
        }
        this.loading = true;
        if (this.stopId) {
            this.stop = allStops[this.stopId];
        }
        this.title = this.stop?.stopName || '';
        this.description = this.description || '';
        this.loaded = true;
        this.loading = false;
    }

    locationUpdated(otherRow) {
        return otherRow?.stop && this.stop?.stopId !== otherRow.stop?.stopId;
    }

    // Validation helpers
    hasValidStop({allStops}) {
        return (!this.loaded || this.stop || allStops[this.stopId || this.stop?.stopId]);
    }

    isValid({allStops}, i) {
        return this.hasValidStop({allStops}) && super.isValid({allStops}, i);
    }

    invalidReason({allStops}, i) {
        if (!allStops[this.stopId || this.stop?.stopId]) {
            return `The stop ${this.title} has been deleted or is invalid`;
        }
        return super.invalidReason({allStops}, i);
    }

    getInvalidMessage() {
        const missing = [];
        if (!this.stopId?.length || !this.stop) missing.push('stop');
        return missing?.length ? `Missing ${missing.join(', ')}` : null;
    }

    getDistance() {
        return 0;
    }

}

export class ShiftBatDeadRow extends ShiftBatLocationRow {
    constructor(props) {
        super(props);
        // console.log("Creating ShiftBatDeadRow")
        this.type = ShiftBatRowType.dead;
        this.route = null; // Store the waypoints in a "BusRoute" so RouteMap.js can understand it.
        this.trip = null;
        this.start = null;
        this.end = null;
        this.duration = -1; // dynamically generated from route duration
        this.distance = 0;
        Object.assign(this, props);
        this.hideTime = true;

        if (!this.startStopId && this.start) {
            this.startStopId = this.start.stopId;
        }
        if (!this.endStopId && this.end) {
            this.endStopId = this.end.stopId;
        }
        if (this.route) {
            this.route = new Deadrun(this.route);
        }

        // Set time to -1 as it is dynamically set in updateRow
        this.time = -1;

    }

    needsUpdate() {
        return !this.loaded && !this.loading;
    }

    createSubKey() {
        let [startStopId, endStopId] = (this.routeId || this.route?.routeId || '').split('#');
        startStopId = startStopId && startStopId !== 'null' ? startStopId : this.startStopId;
        endStopId = endStopId && endStopId !== 'null' ? endStopId : this.endStopId;
        return `${startStopId}#${endStopId}`;
    }

    async updateRow({single, allDeadruns, deadrunModelData, createNew = true}) {
        const time = Date.now();

        // Backwards compatible
        if(!allDeadruns && deadrunModelData) {
            allDeadruns = await deadrunModelData.getAll();
        }

        if (!this.needsUpdate() || !allDeadruns) {
            return;
        }
        if ((!this.start && !this.startStopId) || (!this.end && !this.endStopId)) {
            this.loaded = true;
            return;
        }

        if(!this.routeId?.length && this.startStopId && this.endStopId) {
            this.routeId = `${this.startStopId}#${this.endStopId}#${this.title}`;
        }

        this.loading = true;
        if (!this.route) {
            console.log('Looking up dead running route path');
            const existingDeadrunsArray = allDeadruns[this.createSubKey()] || [];
            const existingDeadruns = keyBy(existingDeadrunsArray, 'routeId');
            if (this.routeId?.length && !existingDeadruns[this.routeId]) {
                existingDeadruns[this.routeId] = existingDeadruns[this.routeId] || allDeadruns[this.routeId];
            }
            if (!single) {
                const route = existingDeadruns[this.routeId] || existingDeadrunsArray?.[0];
                if (route) {
                    this.route = route;
                    this.routeId = route.routeId;
                    this.title = route.routeName;
                    this.description = route.routeDetails;
                    this.duration = route.duration;
                    this.start = this.start || route.startStop;
                    this.end = this.end || route.endStop;
                    this.trip = new Trip(this.route);
                    this.loading = false;
                    this.loaded = true;
                    this.routes = {...existingDeadruns};
                    return;
                }

                if (!createNew) {
                    this.loading = false;
                    this.loaded = true;
                    throw new Error('No route found for deadrun: ' + this.routeId);
                }

                if(!this.getStartLocation() || !this.getEndLocation()) {
                    this.resetStartStop();
                }

                if(!this.getStartLocation() || !this.getEndLocation()) {
                    console.log('No start or end location for deadrun: ', this.routeId);
                    this.loading = false;
                    this.loaded = true;
                    return;
                }

                let deadrun = new Deadrun({
                    start: this.getStartLocation(), end: this.getEndLocation(), routeId: '_'
                });
                try {
                    const result = await getCachedPath('bus', this.getStartLocation(), this.getEndLocation());
                    console.log(result);
                    if (result?.waypoints?.length) {
                        deadrun.waypoints = result.waypoints;
                        deadrun.duration = result.duration;
                        deadrun.distance = result.distance;
                        deadrun.routeName = `Special - ${this.getStartLocation().stopName} to ${this.getEndLocation().stopName}`;
                        await deadrun.buildDirections();
                    }
                } catch (e) {
                    console.log(e, e);
                }
                if (!deadrun?.waypoints?.length) {
                    deadrun.waypoints = [this.getStartLocation(), this.getEndLocation()];
                    deadrun.distance = getDistanceInMetres(this.getStartLocation(), this.getEndLocation());
                    deadrun.duration = this.distance / (config.operator?.opts?.timetable?.avgSpd || 13.8);
                    deadrun.routeName = `As the crow flys - ${this.getStartLocation().stopName} to ${this.getEndLocation().stopName}`;
                }

                this.route = deadrun;
                this.trip = new Trip(deadrun)
                this.title = abbreviate(deadrun.routeName);
                this.start = deadrun.startStop;
                this.end = deadrun.endStop;
                this.duration = deadrun.duration;
                this.description = abbreviate(deadrun.routeDetails)
                this.routes = {...existingDeadruns};
                return 
            }

            this.routes = {...existingDeadruns};
            // Find the route and add it to the row
            this.route = this.routeId ? this.routes[this.routeId] : Object.values(existingDeadruns)[0];
            this.route = this.route || Object.values(existingDeadruns)[0];
        }

        if(!this.route) return

        // this.description = `Deadrun (${toHrsMinsSecs(this.route.duration, false, true, 1)}, ${toKmMs(this.route.distance)})`
        this.routeId = this.route.routeId;
        this.title = abbreviate(this.route.routeName);
        // If route has routeDetails, it means that the directions have been calculated
        if (typeof this.route.routeDetails === 'string') {
            this.description = abbreviate(this.route.routeDetails);
        // } else if (!single && !this.description) {
            // this.route.routeDetails = await this.buildDirections();
            // this.description = abbreviate(this.route.routeDetails);
        }
        this.duration = this.route.duration;
        this.distance = this.route.distance;
        this.hasDirections = true;

        // const thisIdx = findIndex(this.shiftBat.rows, row => row.id === this.id);
        // let prevLocRow = null;
        // for (let i = thisIdx - 1; i >= 0; i--) {
        //     prevLocRow = this.shiftBat.rows[i];
        //     if ([ShiftBatRowType.service, ShiftBatRowType.stop].includes(prevLocRow.type)) {
        //         break;
        //     }
        // }
        // let startST = prevLocRow.trip?.stopTimes[0];
        // if (!startST) {
        //     const startTime = secsSinceMidnightToDayjs((prevLocRow.time || 0) + (prevLocRow.duration || 0));
        //     startST = {departHour: startTime.hour(), departMin: startTime.minute()};
        // }
        // const stopTimes = [{
        //     ...this.route.waypoints[0], ...startST, distance: 0, delta: 0
        // }, last(this.route.waypoints) || this.end];
        // this.route.stopTimes = stopTimes;
        // this.route.stops = stopTimes;
        this.trip = new Trip(this.route);

        this.loaded = true;
        this.loading = false;
        console.log(`Updated dead row ${this.id} for shift ${this.shiftBat.shiftBatNumber} ${this.shiftBat.shiftBatId} in ${Date.now() - time}ms`);
    }

    async reset({allDeadruns}) {
        this.loaded = false;
        this.loading = false;
        this.hasDirections = false;
        this.route = null;
        this.routeId = null;
        this.distance = null;
        this.duration = null;
        this.resetStartStop();

        await this.updateRow({allDeadruns});
        await this.route?.buildDirections();
        this.description = abbreviate(this.route.routeDetails);
        // Set routeId = '_' so that the deadrun will save
        this.route.routeId = "_";
        return this;
    }

    resetStartStop() {
        const thisIdx = findIndex(this.shiftBat.rows, row => row.id === this.id);
        let prevLocRow = null, nextLocRow;
        for (let i = thisIdx - 1; i >= 0; i--) {
            prevLocRow = this.shiftBat.rows[i];
            if (prevLocRow.getEndLocation) {
                break;
            }
        }

        for (let i = thisIdx + 1; i < this.shiftBat.rows.length; i++) {
            nextLocRow = this.shiftBat.rows[i];
            if (nextLocRow.getStartLocation) {
                break;
            }
        }
        this.start = this.start || prevLocRow?.getEndLocation();
        this.end = this.end || nextLocRow?.getStartLocation();
        this.startStopId = this.start?.stopId;
        this.endStopId = this.end?.stopId;
    }
    
    async buildDirections(force) {

        try {
            if (!this.route?.waypoints?.length || (!force && this.hasDirections)) {
                return;
            }
            const chunks = chunk(this.route.waypoints, 1000);
            const result = await mapSeries(chunks, (wps, done) => {
                if (!wps?.length || wps.length < 2) {
                    return done();
                }
                return getCachedDirections(wps).then(data => {
                    if (!data) {
                        return done();
                    }
                    const {directions} = data;
                    if (directions?.length) {
                        // this.directionsRow = new ShiftBatRow({title: directions.join(', ')});
                        // return directions.join(', ');
                        return done(null, directions.join(', '));
                    }
                    done(null, '');
                }).catch(e => {
                    console.log(e, e);
                    done(e);
                });
            });
            return result.join(', ');
        } catch (e) {
            console.log('Could not build directions: ' + e, e);
        }
    }

    getEndTime() {
        // calculate duration to the nearest minute using ceiling
        const duration = this.duration > 0 ? Math.ceil(this.duration / 60) * 60 : 0;
        return this.duration > 0 ? (this.getTime() + duration) : (super.getEndTime() || 0);
    }

    getStartLocation() {
        return this.start;
    }

    getEndLocation() {
        return this.end;
    }

    getDistance() {
        return this.route?.getDistance() || 0;
    }

    getDuration() {
        return this.route?.duration || 0;
    }

    // Validation helpers
    hasStartAndEnd() {
        return (!!this.start && !!this.end);
    }

    hasValidStartEndStops({allStops}) {
        return (!!allStops[this.startStopId] && !!allStops[this.endStopId]);
    }

    isValid({allStops}, i) {
        return !this.loaded || ((this.hasStartAndEnd() || this.hasValidStartEndStops({allStops})) && this.route);
    }

    invalidReason({allStops}, i) {
        if (!allStops[this.startStopId] && !allStops[this.endStopId]) {
            return `The start and end stops have been deleted or are invalid`;
        } else if (!allStops[this.startStopId]) {
            return `The start stop has been deleted or is invalid`;
        } else if (!allStops[this.endStopId]) {
            return `The end stop has been deleted or is invalid`;
        } else if (!this.route) {
            return `The deadrun route cannot be found`;
        } else if(!this.isValid({allStops}, i)) {
            return `The deadrun is invalid`;
        }
    }

    toJson() {
        const {id, distance, duration, title, type, route, start, end} = this;
        
        const deadRowData = {
            id,
            distance,
            duration,
            title,
            type,
            routeId: this.route?.routeId || this.routeId,
            startStopId: this.start?.stopId || this.startStopId,
            endStopId: this.end?.stopId || this.endStopId
        };
        
        return deadRowData;
    }

}

export class ShiftBatNoteRow extends ShiftBatRow {
    constructor(props) {
        super(props);
        // console.log("Creating ShiftBatNoteRow")
        this.type = ShiftBatRowType.noteTimed;
        Object.assign(this, props);
        this.loaded = true;

        // Set time to -1 as it is not required
        if(this.type === ShiftBatRowType.note) {
            this.time = -1;
        }
    }

    needsUpdate() {
        return !this.loaded;
    }

    async updateRow() {
        return false;
        // if (!this.needsUpdate()) {
        //     return;
        // }
        // console.log('Looking up notes');
        // this.notes = (await noteRefModelData.fetchAll()) || {}
        // let note;
        // if (!Object.keys(this.notes).length) {
        //     // note = {refId: "note#", title: "", description: "", priority: ShiftBatRowNotePriority.normal}
        //     // this.notes["note#"] = note
        // }
        // note = this.refId ? (this.notes[this.refId] || note) : null
        // this.title = note?.title || "";
        // this.description = note?.description || "";
        // this.priority = note?.priority || "";
        // this.loaded = true
    }

    isValid(args, i) {
        return this.type === 'Note' ? true : super.isValid(args, i);
    }

}

export class ShiftBatBreakRow extends ShiftBatRow {
    constructor(props) {
        super(props);
        this.duration = 0;
        Object.assign(this, props);
    }

    // Validation helpers
    hasPositiveDuration() {
        return this.duration > 0;
    }

    isValid(args, i) {
        return !this.loaded || (this.hasPositiveDuration() && super.isValid(args, i));
    }

    invalidReason(args, i) {
        return this.duration <= 0 ? 'Break duration must be greater than 0' : super.invalidReason(args, i);
    }

    isDefault() {
        return super.isDefault() && !this.duration;
    }

}


export class ShiftBatInvalidRow extends ShiftBatRow {
    constructor(props) {
        super(props);
        Object.assign(this, props);
    }

    isValid() {
        return false;
    }

}

export class ShiftBatCharterRouteRow extends ShiftBatLocationRow {
    constructor(props) {
        super(props);
        this.type = ShiftBatRowType.charter;
        this.route = new BusRoute(); // Store the waypoints in a "BusRoute" so RouteMap.js can understand it.
        this.trip = null;
        this.start = null;
        this.end = null;
        this.duration = 0;
        this.distance = 0;
        Object.assign(this, props);

        if (this.route) {
            this.route = new BusRoute(this.route);
        }

    }

    needsUpdate() {
        return !this.loaded && !this.loading;
    }

    async updateRow({single, charterModelData}) {
        if (!this.needsUpdate()) {
            return;
        }
        if (!this.startStopId || !this.endStopId) {
            this.loaded = true;
            return;
        }

        this.loading = true;
        console.log('Looking up dead running route path');
        const existingDeadruns = await charterModelData.fetch(this) ?? {};
        if (!single && !existingDeadruns?.length) {
            if (!this.getStartLocation() || !this.getEndLocation()) {
                this.loading = false;
                this.loaded = true;
                return;
            }
            let deadrun = new CharterRouteRun({
                // let deadrun = new Deadrun({
                startStop: this.getStartLocation(), endStop: this.getEndLocation(), routeId: '_'
            });
            try {
                const result = await getCachedPath('bus', this.getStartLocation(), this.getEndLocation());
                console.log(result);
                if (result?.waypoints?.length) {
                    deadrun.waypoints = result.waypoints;
                    deadrun.duration = result.duration;
                    deadrun.distance = result.distance;
                    deadrun.routeName = `Route - ${this.getStartLocation().stopName} to ${this.getEndLocation().stopName}`;

                }
            } catch (e) {
                console.log(e, e);
            }
            if (!deadrun?.waypoints?.length) {
                deadrun.waypoints = [this.getStartLocation(), this.getEndLocation()];
                deadrun.distance = getDistanceInMetres(this.getStartLocation(), this.getEndLocation());
                deadrun.duration = this.distance / (config.operator?.opts?.timetable?.avgSpd || 13.8);
                deadrun.routeName = `As the crow flys - ${this.getStartLocation().stopName} to ${this.getEndLocation().stopName}`;
            }
            deadrun = await charterModelData.save(deadrun, {indirect: true, silent: true});
            existingDeadruns.push(deadrun);
            this.routeId = deadrun.routeId;
        }
        this.routes = keyBy(existingDeadruns, 'routeId');
        // Find the route and add it to the row
        this.route = this.routeId ? this.routes[this.routeId] : existingDeadruns[0];
        // this.description = `Deadrun (${toHrsMinsSecs(this.route.duration, false, true, 1)}, ${toKmMs(this.route.distance)})`
        this.route = this.route || existingDeadruns[0];
        this.routeId = this.route.routeId;
        this.title = abbreviate(this.route.routeName);
        if (this.route.routeDetails) {
            this.description = abbreviate(this.route.routeDetails);
        } else if (!single) {
            this.route.routeDetails = await this.buildDirections();
            await charterModelData.save(this.route, {indirect: true, silent: true});
            this.description = abbreviate(this.route.routeDetails);
        }
        this.duration = this.route.duration;
        this.distance = this.route.distance;
        this.hasDirections = true;

        const thisIdx = findIndex(this.shiftBat.rows, row => row.id === this.id);
        let prevLocRow = null;
        for (let i = thisIdx - 1; i >= 0; i--) {
            prevLocRow = this.shiftBat.rows[i];
            if ([ShiftBatRowType.service, ShiftBatRowType.stop].includes(prevLocRow.type)) {
                break;
            }
        }
        let startST = prevLocRow.trip?.stopTimes[0];
        if (!startST) {
            const startTime = secsSinceMidnightToDayjs((prevLocRow.time || 0) + (prevLocRow.duration || 0));
            startST = {departHour: startTime.hour(), departMin: startTime.minute()};
        }
        const stopTimes = [{
            ...this.route.waypoints[0], ...startST, distance: 0, delta: 0
        }, last(this.route.waypoints) || this.end];
        this.route.stopTimes = stopTimes;
        this.route.stops = stopTimes;
        this.trip = new Trip({stopTimes, stops: stopTimes});

        this.loaded = true;
        this.loading = false;
    }

    async reset({charterModelData}) {
        await charterModelData.delete(this.routeId, true, true);
        this.loaded = false;
        this.loading = false;
        this.hasDirections = false;
        this.route = null;
        this.routeId = null;
        this.distance = null;
        this.duration = null;
        const thisIdx = findIndex(this.shiftBat.rows, row => row.id === this.id);
        let prevLocRow = null, nextLocRow;
        for (let i = thisIdx - 1; i >= 0; i--) {
            prevLocRow = this.shiftBat.rows[i];
            if ([ShiftBatRowType.service, ShiftBatRowType.stop].includes(prevLocRow.type)) {
                break;
            }
        }

        for (let i = thisIdx + 1; i < this.shiftBat.rows.length; i++) {
            nextLocRow = this.shiftBat.rows[i];
            if ([ShiftBatRowType.service, ShiftBatRowType.stop].includes(nextLocRow.type)) {
                break;
            }
        }
        this.start = prevLocRow.getEndLocation();
        this.end = nextLocRow.getStartLocation();
        this.startStopId = this.start.stopId;
        this.endStopId = this.end.stopId;
        await this.updateRow({charterModelData});
        return this;
    }


    async buildDirections(force) {

        try {
            if (!this.route?.waypoints?.length || (!force && this.hasDirections)) {
                return;
            }
            const chunks = chunk(this.route.waypoints, 1000);
            const result = await mapSeries(chunks, (wps, done) => {
                if (!wps?.length || wps.length < 2) {
                    return done();
                }
                return getCachedDirections(wps).then(data => {
                    if (!data) {
                        return done();
                    }
                    const {directions} = data;
                    if (directions?.length) {
                        // this.directionsRow = new ShiftBatRow({title: directions.join(', ')});
                        // return directions.join(', ');
                        return done(null, directions.join(', '));
                    }
                    done(null, '');
                }).catch(e => {
                    console.log(e, e);
                    done(e);
                });
            });
            return result.join(', ');
        } catch (e) {
            console.log('Could not build directions: ' + e, e);
        }
    }

    getStartLocation() {
        return this.start;
    }

    // toTimeString() {
    //     return toHrsMinsSecs(this.duration, false, true)
    // }

    getEndLocation() {
        return this.end;
    }

    getDistance() {
        return this.route?.getDistance() || 0;
    }

    getDuration() {
        return this.route?.getDuration() || 0;
    }

    // Validation helpers
    hasValidStartEndStops({allStops}) {
        return allStops[this.startStopId] && allStops[this.endStopId];
    }

    isValid({allStops}, i) {
        return !this.loaded || (this.hasValidStartEndStops({allStops}) && super.isValid({allStops}, i));
    }

    invalidReason({allStops}, i) {
        if (!allStops[this.startStopId] && !allStops[this.endStopId]) {
            return `The start and end stops have been deleted or are invalid`;
        } else if (!allStops[this.startStopId]) {
            return `The start stop has been deleted or is invalid`;
        } else if (!allStops[this.endStopId]) {
            return `The end stop has been deleted or is invalid`;
        }
        return super.invalidReason({allStops}, i);
    }

    toJson() {
        const {id, distance, duration, title, type} = this;
        
        return {
            id,
            distance,
            duration,
            title,
            type,
            routeId: this.route?.routeId || this.routeId,
            startStopId: this.start?.stopId || this.startStopId,
            endStopId: this.end?.stopId || this.endStopId
        };
    }

}


export class ShiftBatTemplateRow extends ShiftBatRow {
    constructor(props) {
        super(props);
        this.type = ShiftBatRowType.template;
        Object.assign(this, props);

        if (props.template && props.modelData) {
            // TODO: Update to have templates from shiftbat and charters
            this.initialize(props.template, props.modelData);
        }
    }

    async initialize(id, modelData) {
        if (!modelData) return;
        const _template = await modelData.getTemplate(id);
        if (!_template) return;

        this.templateObj = _template;
        this.title = _template.message;
    }

    isValid(args, i) {
        return super.isValid(args, i);
    }

}


export class WorkItem {

    static from(data) {
        return new WorkItem(clone(data));
    }

    constructor(data) {
        const dateStr = data.date?.format?.('YYYYMMDD') || data.date;
        const baseId = data.dutyId || data.duty?.shiftBatId;
        this.workItemId = `${baseId || ('ADHOC_' + ulid())}_${dateStr}_${data.dayOfWeek}`;
        this.date = null;
        this.dayOfWeek = null;
        this.workItemDate = null;
        this.dutyId = null;
        this.duty = null;
        this.workItemName = null;
        this.employeeId = null;
        this.employeeName = null;
        this.vehicleId = null;
        this.vehicleName = null;
        this.startTimeInSecs = null;
        this.endTimeInSecs = null;
        this.startTimeFormatted = null;
        this.endTimeFormatted = null;
        this.scheduledHours = null;
        this.actualStart = null;
        this.actualEnd = null;
        this.actualPayHours = null;
        this.adHoc = false;
        Object.assign(this, data);
        if (!this.dutyId && this.duty) {
            this.dutyId = this.duty.shiftBatId;
        }
        if (this.duty) {
            this.setDutyDetail();
        }

        if (typeof this.date === 'string') {
            this.date = dayjs(this.date, DATE_STRING).startOf('day');
        }

        if (this.date && this.dayOfWeek !== null) {
            this.workItemDate = this.date?.add?.(this.dayOfWeek, 'days');
        }

        // derive formatted times and scheduled hours for ad hoc
        if (!this.duty) {
            if (Number.isFinite(this.startTimeInSecs)) {
                this.startTimeFormatted = toTime(this.startTimeInSecs);
                this.actualStart = this.actualStart ?? this.startTimeInSecs;
            }
            if (Number.isFinite(this.endTimeInSecs)) {
                this.endTimeFormatted = toTime(this.endTimeInSecs);
                this.actualEnd = this.actualEnd ?? this.endTimeInSecs;
            }
            if (Number.isFinite(this.startTimeInSecs) && Number.isFinite(this.endTimeInSecs)) {
                this.scheduledHours = (this.endTimeInSecs - this.startTimeInSecs) / 3600;
            }
        }
    }

    setDutyDetail() {
        this.startTimeInSecs = this.startTimeInSecs || this.duty?.getStartTime();
        this.endTimeInSecs = this.endTimeInSecs || this.duty?.getEndTime();
        this.startTimeFormatted = this.startTimeFormatted || toTime(this.startTimeInSecs);
        this.endTimeFormatted = this.endTimeFormatted || toTime(this.endTimeInSecs);
        this.workItemName = this.workItemName || this.duty?.getName();
        this.scheduledHours = this.duty?.getShiftTime() / 3600;
        this.actualStart = this.actualStart || this.duty?.getStartTime();
        this.actualEnd = this.actualEnd || this.duty?.getEndTime();
    }

    setDate(date) {
        this.date = date;
    }

    /**
     * Set the  employee on the work item
     * @param employee
     */
    setEmployee(employee) {
        this.employee = employee;
        this.employeeId = employee.employeeID;
        this.employeeName = employee.firstName + ' ' + employee.lastName;
    }

    /**
     * Set the vehicle on the work item
     * @param vehicle
     */
    setVehicle(vehicle) {
        this.vehicle = vehicle;
        this.vehicleId = vehicle?.vehicleId;
        this.vehicleName = vehicle?.vehicleName;
    }

    getDuty() {
        return this.duty;
    }

    getName() {
        if (!this.duty) {
            throw new Error('Missing Duty');
        }
        return this.duty.getName();
    }

    getDistance() {
        return this.duty?.getShiftDistance() || 0;
    }

    getDuration() {
        return this.duty?.getShiftTime() || 0;
    }

    getActualDuration() {
        return this.actualEnd - this.actualStart;
    }


    getWage() {
        const rate = this.employee?.payRate || DEFAULT_DRIVER_RATE;
        const hours = this.getPayHours();
        return rate * hours;
    }

    getPayHours() {
        return this.actualPayHours || (this.getActualDuration() / 3600) || this.scheduledHours
    }

    toTimeBlocks(workItemId) {
        if (!this.duty) {
            return [];
        }

        const timeBlocks = this.duty.toTimeBlocks(workItemId) || [];

        // Set the actual start and end times
        if (timeBlocks?.length && Number.isFinite(this.actualStart) && this.actualStart > -1) {
            const startTimeBlock = timeBlocks[0];
            startTimeBlock.startTime = this.actualStart;
        }
        if (timeBlocks?.length && Number.isFinite(this.actualEnd) && this.actualEnd > -1) {
            const endTimeBlock = last(timeBlocks);
            endTimeBlock.endTime = this.actualEnd;
        }
        return timeBlocks.map(timeBlock => {
            timeBlock.id = this.workItemId;
            timeBlock.startTime = secsSinceMidnightToDayjs(timeBlock.startTime, this.workItemDate || this.date);
            timeBlock.endTime = secsSinceMidnightToDayjs(timeBlock.endTime, this.workItemDate || this.date);
            return timeBlock;
        });
    }

    /**
     * Returns a dayjs representing the start of the job, including the deadrun if exists
     */
    getStartTime(opts = {date: dayjs()}) {
        let {date} = opts;
        date = this.date || date;
        if (this.startTime) {
            return this.startTime;
        }
        if (!this.duty) {
            throw new Error('Missing Duty');
        }
        return secsSinceMidnightToDayjs(this.duty.getStartTime(), this.workItemDate || date);
    }

    /**
     Returns a dayjs representing the end of the job, including the deadrun if exists
     */
    getEndTime(opts = {date: dayjs()}) {
        let {date} = opts;
        date = this.date || date;
        if (this.endTime) {
            return this.endTime;
        }
        if (!this.duty) {
            throw new Error('Missing Duty');
        }
        return secsSinceMidnightToDayjs(this.duty.getEndTime(), this.workItemDate || date);
    }

    getTotalWorkingTime(startTime, endTime) {
        // Convert shiftbat time (s) to minutes
        return this.getDuty()?.getShiftTime(startTime, endTime) / 60;
    }

    getTotalRestTime(minimumRestTime) {
        return this.getDuty()?.getTotalRestTime(minimumRestTime);

    }

    getMaxConsecutiveRestTime() {
        return this.getDuty()?.getMaxConsecutiveRestTime();
    }

    clean() {
      // remove any allocation specific information (employee, vehicle, compliance, validations)
      delete this.employee;
      delete this.vehicle;
      delete this.employeeId;
      delete this.vehicleId;
      delete this.employeeName;
      delete this.vehicleName;
      delete this.compliance;
      delete this.validations;
    }

    toCsv() {
        const json = {...this};
        json.week = typeof this.date === 'string' ? this.date : this.date.format(DATE_STRING);
        json.workItemDate = typeof this.workItemDate === 'string' ? this.workItemDate : this.workItemDate.format(DATE_STRING);
        json.wage = this.getWage().toFixed(2);
        delete json.date;
        delete json.employee;
        delete json.vehicle;
        delete json.duty;
        delete json.startTimeInSecs;
        delete json.endTimeInSecs;
        delete json.roster;
        delete json.prevRoster;
        delete json.compliance;
        return json;
    }

    toJson() {
        const workItemData = {...this};

        // Format date fields
        workItemData.date = typeof this.date === 'string'
            ? this.date
            : this.date.format(DATE_STRING);
        workItemData.workItemDate = typeof this.workItemDate === 'string'
            ? this.workItemDate
            : this.workItemDate.format(DATE_STRING);

        // Remove fields that should not be serialised
        const baseRemovals = ['employee', 'vehicle', 'duty', 'employeeName', 'vehicleName', 'roster', 'prevRoster', 'compliance'];
        const adHocSensitive = ['startTimeFormatted', 'endTimeFormatted'];
        const nonAdHocSensitive = ['workItemName', 'startTimeInSecs', 'endTimeInSecs', 'startTimeFormatted', 'endTimeFormatted'];

        const fieldsToRemove = this.adHoc ? baseRemovals.concat(adHocSensitive) : baseRemovals.concat(nonAdHocSensitive);
        fieldsToRemove.forEach(field => {
            delete workItemData[field];
        });

        // Ensure ad hoc items keep essential fields
        if (this.adHoc) {
            workItemData.adHoc = true;
            workItemData.workItemName = this.workItemName;
            workItemData.startTimeInSecs = this.startTimeInSecs;
            workItemData.endTimeInSecs = this.endTimeInSecs;
        }

        return workItemData;
    }

    fromJson(shiftBatsById, employeesById, vehiclesById) {
        if (typeof this.date === 'string') {
            this.date = dayjs(this.date, DATE_STRING).startOf('day');
        }
        if (typeof this.workItemDate === 'string') {
            this.workItemDate = dayjs(this.workItemDate, DATE_STRING).startOf('day');
        }
        if (this.employeeId) {
            const employee = employeesById[`#EMPLOYEE#${this.employeeId}#DETAILS`];
            if (employee) {
                this.employeeName = employee.firstName + ' ' + employee.lastName;
            }
        }
        if (this.vehicleId) {
            this.vehicleName = vehiclesById[this.vehicleId]?.vehicleName || 'Unknown vehicle';
        }

        if (this.dutyId) {
            this.duty = shiftBatsById[this.dutyId];
            this.setDutyDetail();
        }
        return this;
    }

    clone() {
        return WorkItem.from(this);
    }
}
